import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'sonner';
import api from '../api';
import { UserContext } from '../context/UserContext';
import ReglasContables from './ReglasContables';
import DashboardEmpresaContabilidad from './DashboardEmpresaContabilidad';

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE PARA PREVIEW SEGURO DE IMAGEN CON TOKEN
// ─────────────────────────────────────────────────────────────────────────────
function SecureImagePreview({ logId, filename }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let objectUrl;
    api.get(`/contabilidad/logs/${logId}/archivos/${filename}`, { responseType: 'blob' })
      .then(resp => {
        objectUrl = window.URL.createObjectURL(new Blob([resp.data]));
        setUrl(objectUrl);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error cargando preview:', err);
        setLoading(false);
      });
    return () => { if (objectUrl) window.URL.revokeObjectURL(objectUrl); };
  }, [logId, filename]);

  if (loading) return <div className="text-gray-400 text-xs mt-2">Cargando captura...</div>;
  if (!url) return <div className="text-red-400 text-xs mt-2">No se pudo cargar la captura</div>;
  return (
    <div className="mt-3 relative inline-block group">
      <a href={url} target="_blank" rel="noreferrer">
        <img src={url} alt="Captura de error" className="max-w-full md:max-w-md h-auto rounded-lg border-2 border-red-900/50 shadow-lg cursor-pointer hover:opacity-90 transition-opacity" />
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg pointer-events-none">
          <span className="text-white text-sm font-semibold flex items-center gap-2"><i className="material-symbols-outlined">zoom_in</i> Clic para ampliar</span>
        </div>
      </a>
    </div>
  );
}

const MESES = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const estadoConfig = {
  en_progreso: { label: 'En progreso', color: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-500' },
  completado: { label: 'Completado', color: 'bg-green-100 text-green-800', dot: 'bg-green-500' },
  error: { label: 'Error', color: 'bg-red-100 text-red-800', dot: 'bg-red-500' },
  cancelado: { label: 'Cancelado', color: 'bg-gray-100 text-gray-700', dot: 'bg-gray-400' }
};

function getResumenLog(log, tipoFallback = '') {
  const tipo = log?.tipoDocumento || tipoFallback;

  if (!log) {
    return { label: 'Sin ejecutar', color: 'bg-gray-100 text-gray-600 border-gray-200' };
  }

  if (log.estado === 'cancelado') {
    return { label: 'Cancelado', color: 'bg-gray-100 text-gray-700 border-gray-200' };
  }

  if (log.estado === 'en_cola') {
    return { label: 'En cola', color: 'bg-blue-100 text-blue-800 border-blue-200' };
  }

  if (log.estado === 'en_progreso') {
    return { label: 'En progreso', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
  }

  if (log.descargaSii?.sinDatos) {
    return {
      label: tipo === 'ventas' ? 'Sin ventas' : 'Sin compras',
      color: 'bg-slate-100 text-slate-700 border-slate-200'
    };
  }

  if (log.estado === 'error') {
    if (log.ingresoBoletas?.ok === false) return { label: 'Error Boletas', color: 'bg-red-100 text-red-800 border-red-200' };
    if (log.subidaInputsoft?.ok === false) return { label: 'Error Inputsoft', color: 'bg-red-100 text-red-800 border-red-200' };
    if (log.procesamientoCsv?.ok === false) return { label: 'Error CSV', color: 'bg-red-100 text-red-800 border-red-200' };
    if (log.descargaSii?.ok === false) return { label: 'Error SII', color: 'bg-red-100 text-red-800 border-red-200' };
    return { label: 'Error', color: 'bg-red-100 text-red-800 border-red-200' };
  }

  if (log.subidaInputsoft?.ok === false || log.ingresoBoletas?.ok === false) {
    return { label: 'Compl. c/ Errores', color: 'bg-orange-100 text-orange-800 border-orange-200' };
  }

  return { label: 'Completado', color: 'bg-green-100 text-green-800 border-green-200' };
}

function EstadoEmpresaResumen({ empresaId, lastLogMap }) {
  const logCompras = lastLogMap[`${empresaId}-compras`];
  const logVentas = lastLogMap[`${empresaId}-ventas`];
  const resumenCompras = getResumenLog(logCompras, 'compras');
  const resumenVentas = getResumenLog(logVentas, 'ventas');

  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${resumenCompras.color}`}>
        Compras: {resumenCompras.label}
      </span>
      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${resumenVentas.color}`}>
        Ventas: {resumenVentas.label}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
export default function PanelContabilidad() {
  const { currentUser } = useContext(UserContext);
  const isAdmin = currentUser?.role === 'ADMIN';

  // Período global (mes/año)
  const hoy = new Date();
  const mesDefault = hoy.getMonth() === 0 ? 12 : hoy.getMonth();
  const anioDefault = hoy.getMonth() === 0 ? hoy.getFullYear() - 1 : hoy.getFullYear();
  const [mes, setMes] = useState(mesDefault);
  const [anio, setAnio] = useState(anioDefault);

  // Tab activa
  const [tab, setTab] = useState('listas');

  // Datos
  const [listas, setListas] = useState([]);
  const [rentaCompanies, setRentaCompanies] = useState([]);  // todos los clientes con renta=true
  const [configMap, setConfigMap] = useState({});  // { clienteId: ConfigEmpresaContable }
  const [lastLogMap, setLastLogMap] = useState({});  // { clienteId-tipo: log }
  const [logs, setLogs] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);

  // Editor de lista (null = no mostrando, objeto = creando/editando)
  const [editingLista, setEditingLista] = useState(null);

  // Lista activa (vista detalle)
  const [listaActiva, setListaActiva] = useState(null);

  // Dashboard Empresa
  const [empresaDashboard, setEmpresaDashboard] = useState(null);

  // Config empresa expandida (tab "Config Empresas")
  const [expandedConfig, setExpandedConfig] = useState(null);
  const [configSearch, setConfigSearch] = useState('');

  const [configGlobal, setConfigGlobal] = useState({ inputsoftRutEmpresa: '', inputsoftUsuario: '', inputsoftPassword: '', inputsoftPasswordSet: false });
  const [guardandoGlobal, setGuardandoGlobal] = useState(false);

  const [isExecutingBatch, setIsExecutingBatch] = useState(false);
  const [modalLote, setModalLote] = useState(null);
  const [estadosMensuales, setEstadosMensuales] = useState([]);


  useEffect(() => { cargarDatos(); }, [mes, anio, isAdmin]);
  useEffect(() => { if (tab === 'configuracion') cargarConfigGlobal(); }, [tab]);
  useEffect(() => {
    if (!logs.some(log => log.estado === 'en_progreso' || log.estado === 'en_cola')) return undefined;

    const interval = setInterval(() => {
      cargarDatos(true); // silencioso para no parpadear
    }, 5000);

    return () => clearInterval(interval);
  }, [logs]);

  const cargarDatos = async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    try {
      const requests = [
        api.get('/listados/all'),
        api.get('/contabilidad/empresas'),
        api.get('/contabilidad/listas'),
        api.get('/contabilidad/estados', { params: { anio } }),
        api.get('/contabilidad/logs', { params: { anio } }),
      ];
      if (isAdmin) requests.push(api.get('/contabilidad/usuarios'));

      const results = await Promise.allSettled(requests);
      const get = (r) => (r?.status === 'fulfilled' ? r.value : null);

      const clientesRes = get(results[0]);
      const configsRes = get(results[1]);
      const listasRes = get(results[2]);
      const estadosRes = get(results[3]);
      const logsRes = get(results[4]);

      if (isAdmin && get(results[5])) setUsuarios(get(results[5]).data || []);

      // Mapa config por clienteId (se construye primero para filtrar empresas visibles)
      const cMap = {};
      if (configsRes) {
        for (const cfg of (configsRes.data || [])) {
          if (cfg.clienteId?._id) cMap[cfg.clienteId._id] = cfg;
        }
        setConfigMap(cMap);
      }

      // Empresas renta: ADMIN ve todas, FACTURACION solo las asignadas (las que tienen config)
      if (clientesRes) {
        const todasRenta = (clientesRes.data || [])
          .filter(c => c.renta)
          .sort((a, b) => a.nombre.localeCompare(b.nombre));
        const visibles = isAdmin
          ? todasRenta
          : todasRenta.filter(c => cMap[c._id]);
        setRentaCompanies(visibles);
      }

      // Mapa de estados por clienteId-mes-tipo
      if (estadosRes) {
        const estadosData = estadosRes.data || [];
        setEstadosMensuales(estadosData);
        // Construimos un objeto similar a lastLogMap para mantener compatibilidad en la UI
        const lMap = {};
        for (const est of estadosData) {
          const cid = est.clienteId;
          if (!cid) continue;
          
          if (est.ventas?.estado && est.ventas.estado !== 'pendiente') {
            lMap[`${cid}-${est.ventas.ultimoLogId}`] = { estado: est.ventas.estado, tipoDocumento: 'ventas', mes: est.mes };
          }
          if (est.compras?.estado && est.compras.estado !== 'pendiente') {
            lMap[`${cid}-${est.compras.ultimoLogId}`] = { estado: est.compras.estado, tipoDocumento: 'compras', mes: est.mes };
          }
          if (est.honorarios?.estado && est.honorarios.estado !== 'pendiente') {
            lMap[`${cid}-${est.honorarios.ultimoLogId}`] = { estado: est.honorarios.estado, tipoDocumento: 'honorarios', mes: est.mes };
          }
          if (est.centralizacion?.estado && est.centralizacion.estado !== 'pendiente') {
            lMap[`${cid}-${est.centralizacion.ultimoLogId}`] = { estado: est.centralizacion.estado, tipoDocumento: 'centralizacion', mes: est.mes };
          }
        }
        setLastLogMap(lMap);
      }

      if (logsRes) {
        setLogs(logsRes.data || []);
      }

      if (listasRes) {
        setListas(listasRes.data || []);
      }

      // Log de errores parciales en consola
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          const names = ['/listados/all', '/contabilidad/empresas', '/contabilidad/listas', '/contabilidad/estados', '/contabilidad/logs', '/contabilidad/usuarios'];
          console.warn(`[cargarDatos] Falló ${names[i] || i}:`, r.reason?.message || r.reason);
        }
      });
    } catch (err) {
      console.error('Error cargando datos contabilidad:', err);
    } finally {
      if (!silencioso) setLoading(false);
    }
  };

  const cargarConfigGlobal = async () => {
    try {
      const { data } = await api.get('/contabilidad/config-global');
      setConfigGlobal({ ...data, inputsoftPassword: '' });
    } catch (_) { }
  };

  const eliminarLista = async (listaId) => {
    if (!window.confirm('¿Eliminar esta lista? Esta acción no se puede deshacer.')) return;
    try {
      await api.delete(`/contabilidad/listas/${listaId}`);
      toast.success('Lista eliminada');
      cargarDatos();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error eliminando lista');
    }
  };

  const ejecutarLista = async (lista, opcionesLote) => {
    // Filtrar empresas con ID Inputsoft configurado y que estén marcadas para Batch
    const empresasConConfig = (lista.empresas || []).filter(e => {
      const id = e._id || e;
      return configMap[id]?.inputsoftEmpresaId && configMap[id]?.activoBatch;
    }).sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));

    if (empresasConConfig.length === 0) {
      toast.error('Ninguna empresa en la lista tiene configurado el ID de Inputsoft o ninguna está marcada para Batch.');
      return;
    }

    let ok = 0, fail = 0;
    let totalProcesos = 0;
    
    // Pre-calcular cantidad
    for (const empresa of empresasConConfig) {
      const cfg = configMap[empresa._id || empresa] || {};
      const runV = opcionesLote.useDefault ? cfg.ejecutarVentas : opcionesLote.ventas;
      const runC = opcionesLote.useDefault ? cfg.ejecutarCompras : opcionesLote.compras;
      const runH = opcionesLote.useDefault ? cfg.ejecutarHonorarios : opcionesLote.honorarios;
      const runCent = opcionesLote.useDefault ? cfg.centralizarAutomatico : opcionesLote.centralizacion;
      if(runV) totalProcesos++;
      if(runC) totalProcesos++;
      if(runH) totalProcesos++;
      if(runCent) totalProcesos++;
    }

    toast.info(`Iniciando ${totalProcesos} proceso(s) en ${empresasConConfig.length} empresas...`);
    setIsExecutingBatch(true);

    try {
      for (const empresa of empresasConConfig) {
        const id = empresa._id || empresa;
        const cfg = configMap[id] || {};
        
        const runV = opcionesLote.useDefault ? cfg.ejecutarVentas : opcionesLote.ventas;
        const runC = opcionesLote.useDefault ? cfg.ejecutarCompras : opcionesLote.compras;
        const runH = opcionesLote.useDefault ? cfg.ejecutarHonorarios : opcionesLote.honorarios;
        const runCent = opcionesLote.useDefault ? cfg.centralizarAutomatico : opcionesLote.centralizacion;
        const sFetch = opcionesLote.useDefault ? cfg.soloFetch : opcionesLote.soloFetch;

        const tipos = [];
        if (runC) tipos.push('compras');
        if (runV) tipos.push('ventas');
        if (runH) tipos.push('honorarios');
        if (runCent && !sFetch) tipos.push('centralizacion');

        for (const t of tipos) {
          if (!opcionesLote.forzarReejecucion) {
            const estadoDoc = estadosMensuales.find(est => est.clienteId === id && est.mes === parseInt(mes));
            if (estadoDoc && estadoDoc[t]?.estado === 'completado') {
              console.log(`[Batch] Saltando ${empresa.nombre} - ${t} porque ya está completado.`);
              continue;
            }
          }

          try {
            await api.post('/contabilidad/ejecutar', {
              clienteId: id,
              mes: parseInt(mes),
              anio: parseInt(anio),
              tipoDocumento: t,
              soloFetch: sFetch
            });
            ok++;
            await new Promise(r => setTimeout(r, 300));
          } catch (err) {
            fail++;
            console.error(`Error ${empresa.nombre} ${t}:`, err.response?.data?.error);
          }
        }
      }

      toast.success(
        `${ok} proceso(s) iniciados${fail > 0 ? `, ${fail} ya en curso o error` : ''} — ${MESES[mes]} ${anio}`
      );
    } finally {
      setIsExecutingBatch(false);
      setTimeout(cargarDatos, 1000);
    }
  };

  const ejecutarListaMultiMes = async (lista, mesesSeleccionados, tipo) => {
    const empresasConConfig = (lista.empresas || []).filter(e => {
      const id = e._id || e;
      return configMap[id]?.inputsoftEmpresaId && configMap[id]?.activoBatch;
    }).sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));

    if (empresasConConfig.length === 0) {
      toast.error('Ninguna empresa en la lista tiene configurado el ID de Inputsoft o ninguna está marcada para Batch.');
      return;
    }

    const tipos = tipo === 'ambos' ? ['compras', 'ventas'] : [tipo];
    let ok = 0, fail = 0;
    toast.info(`Iniciando Multi-mes para ${empresasConConfig.length * tipos.length} proceso(s)...`);
    setIsExecutingBatch(true);

    try {
      for (const empresa of empresasConConfig) {
        for (const t of tipos) {
          try {
            await api.post('/contabilidad/ejecutar-multimes', {
              clienteId: empresa._id || empresa,
              anio: parseInt(anio),
              meses: mesesSeleccionados,
              tipoDocumento: t
            });
            ok++;
            await new Promise(r => setTimeout(r, 400));
          } catch (err) {
            fail++;
            console.error(`Error Multi-mes ${empresa.nombre} ${t}:`, err.response?.data?.error);
          }
        }
      }

      toast.success(`${ok} procesos encolados Multi-mes${fail > 0 ? `, ${fail} con error` : ''} — Año ${anio}`);
    } finally {
      setIsExecutingBatch(false);
      setTimeout(cargarDatos, 1000);
    }
  };

  const guardarConfigGlobal = async () => {
    setGuardandoGlobal(true);
    try {
      await api.put('/contabilidad/config-global', configGlobal);
      toast.success('Credenciales guardadas');
      cargarConfigGlobal();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error guardando');
    } finally {
      setGuardandoGlobal(false);
    }
  };

  const anioActual = new Date().getFullYear();
  const años = Array.from({ length: 5 }, (_, i) => anioActual - i);

  const filteredConfigCompanies = rentaCompanies.filter(c =>
    !configSearch ||
    c.nombre.toLowerCase().includes(configSearch.toLowerCase()) ||
    c.rut.includes(configSearch)
  );



  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto">

      {/* Vista completa de Dashboard de empresa */}
      {empresaDashboard ? (
        <DashboardEmpresaContabilidad
          empresa={empresaDashboard}
          anio={anio}
          onClose={() => setEmpresaDashboard(null)}
        />
      ) : (
        <>
          {/* Cabecera con período */}
          <div className="flex flex-wrap items-start sm:items-center justify-between gap-3 mb-5">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Contabilidad</h1>
              <p className="text-xs sm:text-sm text-gray-400">Automatización Libro Compras/Ventas SII → Inputsoft</p>
            </div>
            <div className="flex items-center gap-2 bg-white border rounded-xl px-3 py-2 shadow-sm">
              <span className="text-xs text-gray-400 font-medium uppercase tracking-wide hidden sm:inline">Período</span>
              <select value={mes} onChange={e => setMes(Number(e.target.value))}
                className="border-0 text-sm font-semibold text-gray-700 focus:ring-0 bg-transparent">
                {MESES.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
              <select value={anio} onChange={e => setAnio(Number(e.target.value))}
                className="border-0 text-sm font-semibold text-gray-700 focus:ring-0 bg-transparent">
                {años.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-0.5 border-b mb-5 overflow-x-auto">
            {[
              { id: 'listas', label: '📂 Mis Listas' },
              { id: 'historial', label: '📋 Historial' },
              { id: 'configuracion', label: isAdmin ? '⚙ Configuración' : '🔑 Credenciales' },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t.id
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* ── TAB: MIS LISTAS ────────────────────────────────────────────────── */}
          {tab === 'listas' && (
            <div>
              {editingLista ? (
                <ListaEditor
                  lista={editingLista}
                  companies={rentaCompanies}
                  configMap={configMap}
                  onSave={async (data) => {
                    try {
                      if (data._id) {
                        await api.put(`/contabilidad/listas/${data._id}`, data);
                        toast.success('Lista actualizada');
                      } else {
                        await api.post('/contabilidad/listas', data);
                        toast.success('Lista creada');
                      }
                      setEditingLista(null);
                      cargarDatos();
                    } catch (err) {
                      toast.error(err.response?.data?.error || 'Error guardando lista');
                    }
                  }}
                  onCancel={() => setEditingLista(null)}
                />
              ) : listaActiva ? (
                <ListaDetalle
                  lista={listaActiva}
                  mes={mes}
                  anio={anio}
                  rentaCompanies={rentaCompanies}
                  configMap={configMap}
                  estadosMensuales={estadosMensuales}
                  logs={logs}
                  lastLogMap={lastLogMap}
                  usuarios={usuarios}
                  isAdmin={isAdmin}
                  onBack={() => setListaActiva(null)}
                  onGuardado={() => cargarDatos(true)}
                  onEdit={() => {
                    setEditingLista({
                      _id: listaActiva._id,
                      nombre: listaActiva.nombre,
                      descripcion: listaActiva.descripcion,
                      empresas: (listaActiva.empresas || []).map(e => (e._id || e).toString())
                    });
                    setListaActiva(null);
                  }}
                  onDelete={() => { eliminarLista(listaActiva._id); setListaActiva(null); }}
                  onEjecutar={(tipo) => ejecutarLista(listaActiva, tipo)}
                  onEjecutarMultiMes={ejecutarListaMultiMes}
                  isExecutingBatch={isExecutingBatch}
                  onActualizarLista={(listaActualizada) => setListaActiva(listaActualizada)}
                  onOpenDashboard={setEmpresaDashboard}
                />
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-gray-500">
                      {loading ? 'Cargando...' : listas.length === 0 ? 'Sin listas aún.' : `${listas.length} lista(s)`}
                    </p>
                    <button
                      onClick={() => setEditingLista({ nombre: '', descripcion: '', empresas: [] })}
                      className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                      + Nueva Lista
                    </button>
                  </div>

                  {/* Banner de estado de ejecución */}
                  {logs.filter(l => ['en_progreso', 'en_cola'].includes(l.estado)).length > 0 ? (
                    <div className="mb-4 flex items-center justify-between rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="relative flex h-3 w-3">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75"></span>
                          <span className="relative inline-flex h-3 w-3 rounded-full bg-blue-500"></span>
                        </div>
                        <span className="text-sm font-medium text-blue-900">
                          <strong>Batch en ejecución:</strong> {logs.filter(l => ['en_progreso', 'en_cola'].includes(l.estado)).length} procesos en progreso o cola.
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4 flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 shadow-sm">
                      <div className="flex items-center gap-3">
                        <span className="relative inline-flex h-3 w-3 rounded-full bg-gray-400"></span>
                        <span className="text-sm font-medium text-gray-600">
                          Sistema inactivo (sin ejecuciones pendientes)
                        </span>
                      </div>
                    </div>
                  )}

                  {listas.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-xl border">
                      <div className="text-5xl mb-3">📂</div>
                      <p className="text-gray-600 font-medium mb-1">Crea tu primera lista</p>
                      <p className="text-sm text-gray-400 mb-5">
                        Agrupa empresas por tipo de proceso: IVA, RCV, F29, etc.<br />
                        Luego ejecútalas juntas con un solo clic.
                      </p>
                      <button
                        onClick={() => setEditingLista({ nombre: '', descripcion: '', empresas: [] })}
                        className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
                        + Nueva Lista
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {listas.map(lista => (
                        <ListaCard
                          key={lista._id}
                          lista={lista}
                          mes={mes}
                          anio={anio}
                          configMap={configMap}
                          onAbrir={() => setListaActiva(lista)}
                          onEdit={() => setEditingLista({
                            _id: lista._id,
                            nombre: lista.nombre,
                            descripcion: lista.descripcion,
                            empresas: (lista.empresas || []).map(e => (e._id || e).toString())
                          })}
                          onDelete={() => eliminarLista(lista._id)}
                          onEjecutar={() => setModalLote(lista)}
                          onEjecutarMultiMes={ejecutarListaMultiMes}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Modal Lote a nivel Global */}
          {modalLote && (
            <ModalEjecutarLote 
              lista={modalLote} 
              onClose={() => setModalLote(null)} 
              onEjecutar={(opciones) => {
                ejecutarLista(modalLote, opciones);
                setModalLote(null);
              }} 
            />
          )}

          {/* ── TAB: CONFIG EMPRESAS (solo ADMIN, todas las empresas) ──────────── */}
          {tab === 'empresas' && (
            <div>
              <div className="flex flex-wrap gap-3 items-center mb-4">
                <input
                  type="text"
                  value={configSearch}
                  onChange={e => setConfigSearch(e.target.value)}
                  placeholder="Buscar empresa o RUT..."
                  className="flex-1 min-w-0 border rounded-lg px-3 py-2 text-sm" />
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  {Object.keys(configMap).length} configuradas / {rentaCompanies.length} total
                </span>
              </div>

              {loading ? (
                <p className="text-sm text-gray-400 text-center py-10">Cargando...</p>
              ) : filteredConfigCompanies.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-10">Sin resultados.</p>
              ) : (
                <div className="space-y-2">
                  {filteredConfigCompanies.map(c => {
                    const cfg = configMap[c._id];
                    const isExpanded = expandedConfig === c._id;
                    const tieneId = !!cfg?.inputsoftEmpresaId;
                    return (
                      <div key={c._id} className="bg-white border rounded-xl overflow-hidden shadow-sm">
                        <button
                          onClick={() => setExpandedConfig(isExpanded ? null : c._id)}
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${tieneId ? 'bg-green-500' : 'bg-orange-400'}`} />
                            <div className="min-w-0">
                              <div className="font-medium text-gray-800 text-sm truncate">{c.nombre}</div>
                              <div className="text-xs text-gray-400 font-mono">{c.rut}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                            {tieneId ? (
                              <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full font-medium hidden sm:inline">
                                #{cfg.inputsoftEmpresaId}
                              </span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 bg-orange-50 text-orange-600 border border-orange-200 rounded-full hidden sm:inline">
                                Sin configurar
                              </span>
                            )}
                            <span className="text-gray-400 text-xs">{isExpanded ? '▲' : '▼'}</span>
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="px-4 pb-4 border-t bg-gray-50/50">
                            <div className="pt-4">
                              <ConfigEmpresaInline
                                clienteId={c._id}
                                initialConfig={cfg}
                                usuarios={usuarios}
                                isAdmin={isAdmin}
                                onGuardado={cargarDatos}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {/* ── TAB: HISTORIAL ───────────────────────────────────────────────── */}
          {tab === 'historial' && <HistorialLogs logs={logs} onRefresh={cargarDatos} />}

          {/* ── TAB: CONFIGURACIÓN (Credenciales + Config Empresas si Admin) ─── */}
          {tab === 'configuracion' && (
            <div className="space-y-6">
              <div className="bg-white border rounded-xl p-4 sm:p-6 max-w-2xl shadow-sm">
                <h3 className="font-semibold text-gray-700 mb-1">Credenciales Inputsoft</h3>
                <p className="text-xs text-gray-400 mb-4">Usadas para todas las empresas. La contraseña se guarda cifrada.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">RUT empresa operador</label>
                    <input type="text" value={configGlobal.inputsoftRutEmpresa}
                      onChange={e => setConfigGlobal(p => ({ ...p, inputsoftRutEmpresa: e.target.value }))}
                      placeholder="77.596.210-0" className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Usuario</label>
                    <input type="text" value={configGlobal.inputsoftUsuario}
                      onChange={e => setConfigGlobal(p => ({ ...p, inputsoftUsuario: e.target.value }))}
                      placeholder="usuario@empresa.cl" className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Contraseña
                      {configGlobal.inputsoftPasswordSet && (
                        <span className="text-emerald-600 ml-1 font-normal">(guardada ✓)</span>
                      )}
                    </label>
                    <input type="password" value={configGlobal.inputsoftPassword}
                      onChange={e => setConfigGlobal(p => ({ ...p, inputsoftPassword: e.target.value }))}
                      placeholder={configGlobal.inputsoftPasswordSet ? '(sin cambios)' : 'Contraseña'}
                      className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                </div>
                <button onClick={guardarConfigGlobal} disabled={guardandoGlobal}
                  className="mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50">
                  {guardandoGlobal ? 'Guardando...' : 'Guardar credenciales'}
                </button>
              </div>

              {isAdmin && (
                <div>
                  <div className="flex flex-wrap gap-3 items-center mb-4">
                    <h3 className="font-semibold text-gray-700">Configuración por Empresa</h3>
                    <input type="text" value={configSearch}
                      onChange={e => setConfigSearch(e.target.value)}
                      placeholder="Buscar empresa o RUT..."
                      className="flex-1 min-w-0 border rounded-lg px-3 py-2 text-sm max-w-xs" />
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {Object.keys(configMap).length} configuradas / {rentaCompanies.length} total
                    </span>
                  </div>
                  {loading ? (
                    <p className="text-sm text-gray-400 text-center py-10">Cargando...</p>
                  ) : filteredConfigCompanies.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-10">Sin resultados.</p>
                  ) : (
                    <div className="space-y-2">
                      {filteredConfigCompanies.map(c => {
                        const cfg2 = configMap[c._id];
                        const isExp2 = expandedConfig === c._id;
                        const tieneId2 = !!cfg2?.inputsoftEmpresaId;
                        return (
                          <div key={c._id} className="bg-white border rounded-xl overflow-hidden shadow-sm">
                            <button onClick={() => setExpandedConfig(isExp2 ? null : c._id)}
                              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left">
                              <div className="flex items-center gap-3 min-w-0">
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${tieneId2 ? 'bg-green-500' : 'bg-orange-400'}`} />
                                <div className="min-w-0">
                                  <div className="font-medium text-gray-800 text-sm truncate">{c.nombre}</div>
                                  <div className="text-xs text-gray-400 font-mono">{c.rut}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                {tieneId2
                                  ? <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full font-medium hidden sm:inline">#{cfg2.inputsoftEmpresaId}</span>
                                  : <span className="text-xs px-2 py-0.5 bg-orange-50 text-orange-600 border border-orange-200 rounded-full hidden sm:inline">Sin configurar</span>
                                }
                                <span className="text-gray-400 text-xs">{isExp2 ? '▲' : '▼'}</span>
                              </div>
                            </button>
                            {isExp2 && (
                              <div className="px-4 pb-4 border-t bg-gray-50/50">
                                <div className="pt-4">
                                  <ConfigEmpresaInline clienteId={c._id} initialConfig={cfg2}
                                    usuarios={usuarios} isAdmin={isAdmin} onGuardado={cargarDatos} />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// TARJETA DE LISTA
// ─────────────────────────────────────────────────────────────────────────────
function ListaCard({ lista, mes, anio, configMap, onAbrir, onEdit, onDelete, onEjecutar, onEjecutarMultiMes }) {
  const [showTipo, setShowTipo] = useState(false);
  const [showMultiMes, setShowMultiMes] = useState(false);
  const [mesesMulti, setMesesMulti] = useState([mes]);
  const [ejecutando, setEjecutando] = useState(false);

  // Checkboxes state
  const [chkCompras, setChkCompras] = useState(true);
  const [chkVentas, setChkVentas] = useState(true);
  const [chkHonorarios, setChkHonorarios] = useState(false);
  const [chkCentralizacion, setChkCentralizacion] = useState(false);

  const totalEmpresas = lista.empresas?.length || 0;
  const aptasBatch = (lista.empresas || []).filter(e => {
    const cfg = configMap[(e._id || e).toString()];
    return cfg?.inputsoftEmpresaId && cfg?.activoBatch;
  }).length;
  const puedeEjecutar = aptasBatch > 0;

  const handleEjecutarSeleccion = async (isMultiMes) => {
    if (isMultiMes && mesesMulti.length === 0) return;
    setEjecutando(true);
    
    const tipos = [];
    if (chkCompras && chkVentas) tipos.push('ambos');
    else if (chkCompras) tipos.push('compras');
    else if (chkVentas) tipos.push('ventas');

    if (chkHonorarios) tipos.push('honorarios');
    if (chkCentralizacion && !chkCompras && !chkVentas) tipos.push('centralizacion');

    if (tipos.length === 0) {
      setEjecutando(false);
      return; // Nada que ejecutar
    }

    try {
      for (const tipo of tipos) {
        if (isMultiMes) await onEjecutarMultiMes(lista, mesesMulti, tipo);
        else await onEjecutar(tipo);
      }
    } finally {
      setEjecutando(false);
      setShowTipo(false);
      setShowMultiMes(false);
    }
  };

  const handleEjecutarTodo = async (isMultiMes) => {
    setChkCompras(true);
    setChkVentas(true);
    setChkHonorarios(true);
    setChkCentralizacion(true);
    // Ejecuta "ambos", "honorarios", "centralizacion" (internamente)
    if (isMultiMes && mesesMulti.length === 0) return;
    setEjecutando(true);
    try {
      if (isMultiMes) {
        await onEjecutarMultiMes(lista, mesesMulti, 'ambos');
        await onEjecutarMultiMes(lista, mesesMulti, 'honorarios');
        // Centralización va implícita en ambos o se podría lanzar aparte si hiciera falta.
      } else {
        await onEjecutar('ambos');
        await onEjecutar('honorarios');
      }
    } finally {
      setEjecutando(false);
      setShowTipo(false);
      setShowMultiMes(false);
    }
  };

  return (
    <div className="bg-white border rounded-2xl shadow-sm hover:shadow-md transition-all flex flex-col group relative overflow-hidden">
      {/* Decorative top border */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity" />

      {/* Cuerpo — clic abre detalle */}
      <button onClick={onAbrir} className="p-5 flex-1 text-left flex flex-col">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-bold text-gray-800 text-lg leading-tight group-hover:text-blue-700 transition-colors">{lista.nombre}</h3>
          <span className="bg-blue-50 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ml-2 border border-blue-100">
            {totalEmpresas} emp
          </span>
        </div>

        {lista.descripcion && (
          <p className="text-sm text-gray-500 mb-4 line-clamp-2 leading-relaxed">{lista.descripcion}</p>
        )}

        {/* Status Pills */}
        <div className="flex flex-wrap gap-2 mt-auto pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1.5 bg-gray-50 text-gray-600 text-xs px-2.5 py-1 rounded-lg border">
            <span className={`w-2 h-2 rounded-full ${aptasBatch === totalEmpresas && totalEmpresas > 0 ? 'bg-emerald-400' : (aptasBatch > 0 ? 'bg-yellow-400' : 'bg-red-400')}`} />
            <span className="font-medium">{aptasBatch}/{totalEmpresas} en Batch</span>
          </div>
        </div>
      </button>

      {/* Acciones */}
      <div className="bg-gray-50/80 px-4 py-3 border-t">
        {!showTipo && !showMultiMes ? (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowTipo(true)} disabled={!puedeEjecutar}
              title={!puedeEjecutar ? 'Configura al menos una empresa primero' : `Ejecutar manualmente ${MESES[mes]} ${anio}`}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed">
              ▶ Ejecutar
            </button>
            <button onClick={() => setShowMultiMes(true)} disabled={!puedeEjecutar}
              title="Ejecutar varios meses a la vez"
              className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed">
              Multi-mes
            </button>
            <div className="flex gap-1">
              <button onClick={onEdit} title="Editar lista"
                className="p-2 border border-gray-300 hover:bg-white hover:border-gray-400 text-gray-500 rounded-xl transition-colors">
                <i className="fa-solid fa-pen text-sm" />
              </button>
              <button onClick={onDelete} title="Eliminar lista"
                className="p-2 border border-red-100 bg-red-50 hover:bg-red-100 hover:border-red-200 text-red-500 rounded-xl transition-colors">
                <i className="fa-solid fa-trash text-sm" />
              </button>
            </div>
          </div>
        ) : showMultiMes ? (
          <div className="animate__animated animate__fadeIn animate__faster">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-indigo-700 font-bold">Selecciona meses ({anio})</p>
              <button onClick={() => setShowMultiMes(false)} className="text-xs text-gray-400 hover:text-gray-600">✕ Cancelar</button>
            </div>
            <div className="grid grid-cols-6 gap-1 mb-2">
              {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                <button key={m} 
                  onClick={() => setMesesMulti(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m])}
                  className={`py-1 text-[10px] font-bold rounded border ${mesesMulti.includes(m) ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white text-gray-600 border-gray-300 hover:bg-indigo-50'}`}>
                  {m}
                </button>
              ))}
            </div>
            {/* Checkboxes UI para Multi-Mes */}
            <div className="grid grid-cols-2 gap-2 mb-3 bg-white p-2 rounded border">
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input type="checkbox" checked={chkCompras} onChange={e => setChkCompras(e.target.checked)} className="rounded" /> Compras
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input type="checkbox" checked={chkVentas} onChange={e => setChkVentas(e.target.checked)} className="rounded" /> Ventas
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input type="checkbox" checked={chkHonorarios} onChange={e => setChkHonorarios(e.target.checked)} className="rounded" /> Honorarios
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input type="checkbox" checked={chkCentralizacion} onChange={e => setChkCentralizacion(e.target.checked)} className="rounded" /> Centraliz.
              </label>
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleEjecutarSeleccion(true)} disabled={ejecutando || mesesMulti.length === 0 || (!chkCompras && !chkVentas && !chkHonorarios && !chkCentralizacion)}
                className="flex-1 py-1.5 text-xs font-bold rounded bg-indigo-600 hover:bg-indigo-700 text-white transition-colors disabled:opacity-50">
                ▶ Ejecutar Selección
              </button>
              <button onClick={() => handleEjecutarTodo(true)} disabled={ejecutando || mesesMulti.length === 0}
                className="flex-1 py-1.5 text-xs font-bold rounded bg-yellow-500 hover:bg-yellow-600 text-white transition-colors disabled:opacity-50" title="Ejecutar TODO: Compras, Ventas, Honorarios, Centralización">
                🌟 Ejecutar Todo
              </button>
            </div>
          </div>
        ) : (
          <div className="animate__animated animate__fadeIn animate__faster">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500 font-medium">Ejecutar: <span className="text-blue-600">{MESES[mes]} {anio}</span></p>
              <button onClick={() => setShowTipo(false)} className="text-xs text-gray-400 hover:text-gray-600">✕ Cancelar</button>
            </div>
            {/* Checkboxes UI para Un Mes */}
            <div className="grid grid-cols-2 gap-2 mb-3 bg-white p-2 rounded border">
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input type="checkbox" checked={chkCompras} onChange={e => setChkCompras(e.target.checked)} className="rounded accent-emerald-600" /> Compras
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input type="checkbox" checked={chkVentas} onChange={e => setChkVentas(e.target.checked)} className="rounded accent-emerald-600" /> Ventas
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input type="checkbox" checked={chkHonorarios} onChange={e => setChkHonorarios(e.target.checked)} className="rounded accent-emerald-600" /> Honorarios
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input type="checkbox" checked={chkCentralizacion} onChange={e => setChkCentralizacion(e.target.checked)} className="rounded accent-emerald-600" /> Centraliz.
              </label>
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleEjecutarSeleccion(false)} disabled={ejecutando || (!chkCompras && !chkVentas && !chkHonorarios && !chkCentralizacion)}
                className="flex-1 py-1.5 text-xs font-bold rounded bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-50">
                ▶ Ejecutar Selección
              </button>
              <button onClick={() => handleEjecutarTodo(false)} disabled={ejecutando}
                className="flex-1 py-1.5 text-xs font-bold rounded bg-yellow-500 hover:bg-yellow-600 text-white transition-colors disabled:opacity-50" title="Ejecutar TODO: Compras, Ventas, Honorarios, Centralización">
                🌟 Ejecutar Todo
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DETALLE DE LISTA (vista completa al abrir una lista)
// ─────────────────────────────────────────────────────────────────────────────
function ListaDetalle({ lista, mes, anio, rentaCompanies, configMap, estadosMensuales, logs, lastLogMap, usuarios, isAdmin, onBack, onEdit, onDelete,
  onEjecutar,
  onEjecutarMultiMes,
  isExecutingBatch,
  onGuardado,
  onActualizarLista,
  onOpenDashboard
}) {
  const [subTab, setSubTab] = useState('empresas');
  const [expandedConfig, setExpandedConfig] = useState(null);
  const [ejecutando, setEjecutando] = useState(false);
  const [playMenuId, setPlayMenuId] = useState(null);
  const [guardandoBatchId, setGuardandoBatchId] = useState('');
  const [showMultiMes, setShowMultiMes] = useState(false);
  const [mesesMulti, setMesesMulti] = useState([mes]);

  const [multiMesEmpresaId, setMultiMesEmpresaId] = useState(null);
  const [mesesMultiEmpresa, setMesesMultiEmpresa] = useState([mes]);
  const [manualOpciones, setManualOpciones] = useState({
    ventas: true,
    compras: true,
    honorarios: false,
    centralizacion: false,
    soloFetch: false
  });

  const toggleBatch = async (clienteId, activoBatch) => {
    const cfg = configMap[clienteId] || {};
    setGuardandoBatchId(clienteId);
    try {
      await api.put(`/contabilidad/empresas/${clienteId}`, {
        inputsoftEmpresaId: cfg.inputsoftEmpresaId || '',
        sucursalId: cfg.sucursalId || '',
        cuentaDefaultCompra: cfg.cuentaDefaultCompra || '',
        cuentaDefaultVenta: cfg.cuentaDefaultVenta || '',
        modoImpuestos: cfg.modoImpuestos || 'exento',
        impuestosAMantener: cfg.impuestosAMantener || [],
        activoBatch,
        notas: cfg.notas || '',
        usuariosAsignados: (cfg.usuariosAsignados || []).map(u => u._id || u)
      });
      toast.success(activoBatch ? 'Empresa agregada al batch' : 'Empresa quitada del batch');
      onGuardado();
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo actualizar el batch');
    } finally {
      setGuardandoBatchId('');
    }
  };

  const ejecutarCustom = async (clienteId, overrideMes, overrideAnio) => {
    setPlayMenuId(null);
    setMultiMesEmpresaId(null);
    
    const tipos = [];
    if (manualOpciones.compras) tipos.push('compras');
    if (manualOpciones.ventas) tipos.push('ventas');
    if (manualOpciones.honorarios) tipos.push('honorarios');
    if (manualOpciones.centralizacion && !manualOpciones.soloFetch) tipos.push('centralizacion');

    if (tipos.length === 0) return toast.error('Selecciona al menos un proceso');

    const targetMes = overrideMes || parseInt(mes);
    const targetAnio = overrideAnio || parseInt(anio);
    let ok = 0, fail = 0;
    toast.info(`Iniciando ${tipos.length} proceso(s)...`);
    for (const t of tipos) {
      try {
        await api.post('/contabilidad/ejecutar', {
          clienteId, mes: targetMes, anio: targetAnio, tipoDocumento: t, soloFetch: manualOpciones.soloFetch
        });
        ok++;
      } catch (err) {
        fail++;
        console.error(`Error ejecución manual ${t}:`, err.response?.data?.error);
      }
    }
    toast.success(`${ok} proceso(s) iniciados${fail > 0 ? `, ${fail} error(es)` : ''}`);
    setTimeout(onGuardado, 2000);
  };

  const ejecutarMultiMesCustom = async (clienteId) => {
    if (mesesMultiEmpresa.length === 0) return toast.error('Selecciona al menos un mes');
    setMultiMesEmpresaId(null);
    setPlayMenuId(null);
    
    const tipos = [];
    if (manualOpciones.compras) tipos.push('compras');
    if (manualOpciones.ventas) tipos.push('ventas');
    if (manualOpciones.honorarios) tipos.push('honorarios');
    if (manualOpciones.centralizacion && !manualOpciones.soloFetch) tipos.push('centralizacion');

    if (tipos.length === 0) return toast.error('Selecciona al menos un proceso');
    
    let ok = 0, fail = 0;
    toast.info(`Iniciando Multi-mes para 1 empresa (${tipos.length} proceso/s)...`);
    for (const t of tipos) {
      try {
        await api.post('/contabilidad/ejecutar-multimes', {
          clienteId,
          anio: parseInt(anio),
          meses: mesesMultiEmpresa,
          tipoDocumento: t,
          soloFetch: manualOpciones.soloFetch
        });
        ok++;
      } catch (err) {
        fail++;
        console.error(`Error Multi-mes individual ${t}:`, err);
        toast.error(err.response?.data?.error || `Error en ejecución multi-mes ${t}`);
      }
    }
    toast.success(`${ok} procesos multi-mes encolados${fail > 0 ? `, ${fail} error(es)` : ''}`);
    setTimeout(onGuardado, 2000);
  };

  const cambiarEstadoRapido = async (logId, nuevoEstado) => {
    if (!logId) return toast.error('No hay registro para este mes aún');
    try {
      await api.put(`/contabilidad/logs/${logId}/estado`, { estado: nuevoEstado });
      toast.success(`Estado cambiado a ${nuevoEstado}`);
      if (onGuardado) onGuardado();
    } catch (err) {
      toast.error('No se pudo cambiar el estado');
    }
  };

  const empresasLista = (lista.empresas || []).map(e => {
    if (e.nombre) return e;
    return rentaCompanies.find(c => c._id === (e._id || e)) || { _id: e._id || e, nombre: '—', rut: '' };
  }).sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));

  const totalEmpresas = empresasLista.length;
  const aptasBatch = empresasLista.filter(e => {
    const cfg = configMap[(e._id || e._id?.toString())];
    return cfg?.inputsoftEmpresaId && cfg?.activoBatch;
  }).length;
  const configuradas = empresasLista.filter(e => !!configMap[(e._id || e._id?.toString())]?.inputsoftEmpresaId).length;
  const puedeEjecutar = aptasBatch > 0;

  const idsLista = new Set(empresasLista.map(e => (e._id || e).toString()));
  const estadosLista = estadosMensuales.filter(est => idsLista.has(est.clienteId));
  const logsLista = logs.filter(l => l.clienteId?._id && idsLista.has(l.clienteId._id.toString()));



  return (
    <div>
      {/* ── Cabecera ── */}
      <div className="bg-white border rounded-xl shadow-sm px-4 py-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0">
            ← Volver
          </button>
          <span className="text-gray-200">|</span>
          <div className="min-w-0 flex-1">
            <h2 className="font-bold text-gray-800 text-base truncate">{lista.nombre}</h2>
            {lista.descripcion && <p className="text-xs text-gray-400 truncate">{lista.descripcion}</p>}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto flex-wrap justify-end">
            <span className="text-xs text-gray-400 hidden sm:inline">{totalEmpresas} empresa{totalEmpresas !== 1 ? 's' : ''} · {configuradas} configuradas</span>
            <button onClick={onEdit}
              className="px-3 py-1.5 text-xs border rounded-lg text-gray-500 hover:bg-gray-50 transition-colors">
              ✏ Editar
            </button>
            <button onClick={onDelete}
              className="px-3 py-1.5 text-xs border border-red-200 rounded-lg text-red-400 hover:bg-red-50 transition-colors">
              🗑
            </button>
          </div>
        </div>

        {/* Botón Ejecutar Batch — siempre visible */}
        <div className="mt-3 pt-3 border-t">
          {!showMultiMes ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500 font-medium">
                Ejecutar {aptasBatch > 0 ? aptasBatch : 'N/A'} empresa(s) en batch — <span className="text-blue-600">{MESES[mes]} {anio}</span>
              </span>
              <div className="flex gap-1.5 ml-auto flex-wrap">
                <button onClick={() => onEjecutar(lista)}
                  disabled={!puedeEjecutar || isExecutingBatch}
                  title={!puedeEjecutar ? 'Marca empresas en batch primero (sub-tab Batch)' : ''}
                  className="px-6 py-1.5 text-xs font-bold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  {isExecutingBatch ? 'Iniciando...' : `▶ Ejecutar Lote`}
                </button>
                <button onClick={() => setShowMultiMes(true)} disabled={!puedeEjecutar || isExecutingBatch}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  Multi-mes
                </button>
              </div>
            </div>
          ) : (
            <div className="animate__animated animate__fadeIn animate__faster">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-indigo-700 font-bold">Multi-mes en batch — selecciona meses ({anio}):</span>
                <button onClick={() => setShowMultiMes(false)} className="text-xs text-gray-400 hover:text-gray-600">✕ Cancelar</button>
              </div>
              <div className="flex flex-wrap gap-1 mb-3">
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                  <button key={m}
                    onClick={() => setMesesMulti(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m])}
                    className={`px-2 py-1 text-xs font-bold rounded border ${mesesMulti.includes(m) ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white text-gray-600 border-gray-300 hover:bg-indigo-50'}`}>
                    {MESES[m].substring(0,3)}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-1.5 mb-3 bg-white p-2 rounded border text-xs">
                {[
                  { key: 'compras', label: 'Compras' },
                  { key: 'ventas', label: 'Ventas' },
                  { key: 'honorarios', label: 'Honorarios' },
                  { key: 'centralizacion', label: 'Centralizacion' },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox"
                      checked={manualOpciones[key] ?? (key === 'compras' || key === 'ventas')}
                      onChange={e => setManualOpciones(prev => ({ ...prev, [key]: e.target.checked }))}
                      className="rounded" />
                    {label}
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  disabled={isExecutingBatch || mesesMulti.length === 0 || (!manualOpciones.compras && !manualOpciones.ventas && !manualOpciones.honorarios && !manualOpciones.centralizacion)}
                  onClick={() => {
                    const tipos = [];
                    if (manualOpciones.compras) tipos.push('compras');
                    if (manualOpciones.ventas) tipos.push('ventas');
                    if (manualOpciones.honorarios) tipos.push('honorarios');
                    if (manualOpciones.centralizacion) tipos.push('centralizacion');
                    tipos.forEach(t => onEjecutarMultiMes(lista, mesesMulti, t));
                    setShowMultiMes(false);
                  }}
                  className="flex-1 py-1.5 text-xs font-bold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition-colors disabled:opacity-50">
                  Ejecutar Multi-mes
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Sub-tabs ── */}
      <div className="flex gap-0.5 border-b mb-4 overflow-x-auto">
        {[
          { id: 'empresas', label: `🏢 Empresas (${totalEmpresas})` },
          { id: 'config', label: `⚙ Configuración` },
          ...(isAdmin ? [{ id: 'batch', label: `🚀 Batch (${aptasBatch}/${totalEmpresas})` }] : []),
          { id: 'historial', label: `📋 Historial (${logsLista?.length || 0})` },
        ].map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            className={`px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${subTab === t.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Sub-tab: Empresas ── */}
      {subTab === 'empresas' && (
        empresasLista.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border">
            <p className="text-gray-400 text-sm mb-3">Esta lista no tiene empresas.</p>
            <button onClick={onEdit} className="text-blue-600 text-sm hover:underline">
              Editar lista para agregar
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {empresasLista.map(e => {
              const id = (e._id || e).toString();
              const cfg = configMap[id];
              const tieneId = !!cfg?.inputsoftEmpresaId;
              const enBatch = !!cfg?.activoBatch;
              
              const estadoDoc = estadosLista.find(est => est.clienteId === id && est.mes === parseInt(mes));
              const logC = estadoDoc?.compras?.estado && estadoDoc.compras.estado !== 'pendiente' ? { estado: estadoDoc.compras.estado, _id: estadoDoc.compras.ultimoLogId } : null;
              const logV = estadoDoc?.ventas?.estado && estadoDoc.ventas.estado !== 'pendiente' ? { estado: estadoDoc.ventas.estado, _id: estadoDoc.ventas.ultimoLogId } : null;
              const logH = estadoDoc?.honorarios?.estado && estadoDoc.honorarios.estado !== 'pendiente' ? { estado: estadoDoc.honorarios.estado, _id: estadoDoc.honorarios.ultimoLogId } : null;

              const getColor = (log) => {
                if (!log) return 'text-gray-300';
                if (log.estado === 'cancelado') return 'text-gray-400';
                if (log.estado === 'completado' || log.estado === 'finalizado') return 'text-green-600';
                if (log.estado === 'error') return 'text-red-500';
                if (log.estado === 'en_progreso') return 'text-yellow-500 animate-pulse';
                if (log.estado === 'en_cola') return 'text-blue-500';
                return 'text-gray-400';
              };
              const getIcon = (log) => {
                if (!log) return '—';
                if (log.estado === 'cancelado') return '🛑';
                if (log.estado === 'completado' || log.estado === 'finalizado') return '✓';
                if (log.estado === 'error') return '✗';
                if (log.estado === 'en_progreso') return '⟳';
                if (log.estado === 'en_cola') return '⏳';
                return '?';
              };

              return (
                <div key={id} className="bg-white border rounded-xl px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-3">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${tieneId ? (enBatch ? 'bg-emerald-500' : 'bg-green-400') : 'bg-orange-400'}`} />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-800 text-sm truncate">{e.nombre}</div>
                      <div className="text-xs text-gray-400 font-mono">{e.rut}</div>
                    </div>

                    {/* Estado compras/ventas del período actual */}
                    <div className="flex items-center gap-3 flex-shrink-0 text-xs">
                      <div className="text-center hidden sm:block">
                        <div className="text-gray-400 text-[10px] uppercase tracking-wide">Compras</div>
                        <span className={`font-bold flex items-center justify-center gap-0.5 ${getColor(logC)}`}>
                          {getIcon(logC)}
                          <SelectEstadoMini logId={logC?._id} onCambiarEstado={cambiarEstadoRapido} />
                        </span>
                      </div>
                      <div className="text-center hidden sm:block">
                        <div className="text-gray-400 text-[10px] uppercase tracking-wide">Ventas</div>
                        <span className={`font-bold flex items-center justify-center gap-0.5 ${getColor(logV)}`}>
                          {getIcon(logV)}
                          <SelectEstadoMini logId={logV?._id} onCambiarEstado={cambiarEstadoRapido} />
                        </span>
                      </div>
                      <div className="text-center hidden sm:block">
                        <div className="text-gray-400 text-[10px] uppercase tracking-wide">Honorarios</div>
                        <span className={`font-bold flex items-center justify-center gap-0.5 ${getColor(logH)}`}>
                          {getIcon(logH)}
                          <SelectEstadoMini logId={logH?._id} onCambiarEstado={cambiarEstadoRapido} />
                        </span>
                      </div>
                      {tieneId && (
                        <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full font-medium hidden md:inline">
                          #{cfg.inputsoftEmpresaId}
                        </span>
                      )}
                      {tieneId && isAdmin ? (
                        <button
                          onClick={() => toggleBatch(id, !enBatch)}
                          disabled={guardandoBatchId === id}
                          className={`text-xs px-2 py-0.5 border rounded-full transition-colors disabled:opacity-50 hidden lg:inline ${
                            enBatch ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          {guardandoBatchId === id ? '...' : (enBatch ? '✓ Batch' : '+ Batch')}
                        </button>
                      ) : enBatch && (
                        <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full hidden lg:inline">
                          Batch
                        </span>
                      )}
                      
                      {tieneId && (
                        <div className="relative">
                          <button onClick={() => {
                            if (playMenuId === id) setPlayMenuId(null);
                            else { setPlayMenuId(id); setMultiMesEmpresaId(null); }
                          }}
                            className="text-xs border border-blue-200 rounded-lg px-2 py-1 text-blue-600 hover:bg-blue-50 transition-colors"
                            title="Ejecutar esta empresa">
                            ▶
                          </button>
                          {playMenuId === id && (
                            <div className="absolute right-0 mt-1 w-56 bg-white border rounded-lg shadow-lg z-20 overflow-hidden">
                              <div className="bg-slate-800 text-white text-[10px] font-bold px-3 py-1.5 flex justify-between items-center">
                                OPCIONES DE EJECUCIÓN
                              </div>
                              <div className="p-3 space-y-2.5 bg-gray-50/50">
                                <label className="flex items-center gap-2 cursor-pointer group">
                                  <input type="checkbox" checked={manualOpciones.compras} onChange={e => setManualOpciones(p => ({...p, compras: e.target.checked}))} className="accent-blue-600 rounded w-3.5 h-3.5" />
                                  <span className="text-xs font-semibold text-gray-700 group-hover:text-gray-900">Compras</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer group">
                                  <input type="checkbox" checked={manualOpciones.ventas} onChange={e => setManualOpciones(p => ({...p, ventas: e.target.checked}))} className="accent-blue-600 rounded w-3.5 h-3.5" />
                                  <span className="text-xs font-semibold text-gray-700 group-hover:text-gray-900">Ventas</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer group">
                                  <input type="checkbox" checked={manualOpciones.honorarios} onChange={e => setManualOpciones(p => ({...p, honorarios: e.target.checked}))} className="accent-blue-600 rounded w-3.5 h-3.5" />
                                  <span className="text-xs font-semibold text-gray-700 group-hover:text-gray-900">Honorarios</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer group">
                                  <input type="checkbox" checked={manualOpciones.centralizacion} onChange={e => setManualOpciones(p => ({...p, centralizacion: e.target.checked}))} disabled={manualOpciones.soloFetch} className="accent-blue-600 rounded w-3.5 h-3.5 disabled:opacity-40" />
                                  <span className={`text-xs font-semibold group-hover:text-gray-900 ${manualOpciones.soloFetch ? 'text-gray-400' : 'text-gray-700'}`}>Centralización</span>
                                </label>
                              </div>
                              <div className="px-3 py-2 bg-slate-100 border-y border-slate-200 space-y-2">
                                <label className="flex items-center gap-2 cursor-pointer group">
                                  <input type="checkbox" checked={manualOpciones.soloFetch} onChange={e => setManualOpciones(p => ({...p, soloFetch: e.target.checked, centralizacion: e.target.checked ? false : p.centralizacion}))} className="accent-slate-600 rounded w-3.5 h-3.5" />
                                  <span className="text-[11px] font-bold text-slate-700">☁️ Solo Descargar</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer group">
                                  <input type="checkbox" checked={manualOpciones.forzarReejecucion} onChange={e => setManualOpciones(p => ({...p, forzarReejecucion: e.target.checked}))} className="accent-yellow-600 rounded w-3.5 h-3.5" />
                                  <span className="text-[11px] font-bold text-yellow-700">⚡ Forzar Re-ejecución</span>
                                </label>
                              </div>
                              <div className="p-2 flex gap-1.5 bg-white">
                                <button onClick={() => ejecutarCustom(id, null, null)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold py-1.5 rounded-md transition-colors shadow-sm">▶ Ejecutar</button>
                                <button onClick={() => { setMultiMesEmpresaId(id); setMesesMultiEmpresa([mes]); setPlayMenuId(null); }} className="flex-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 text-[11px] font-bold py-1.5 rounded-md transition-colors shadow-sm">Multi-mes</button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <button onClick={() => onOpenDashboard(e)}
                        className="text-xs border border-purple-200 rounded-lg px-2 py-1 text-purple-600 hover:bg-purple-50 transition-colors">
                        📊
                      </button>
                      <button onClick={() => { setExpandedConfig(id); setSubTab('config'); }}
                        className="text-xs border border-gray-200 rounded-lg px-2 py-1 text-gray-500 hover:bg-gray-50 transition-colors">
                        ⚙
                      </button>
                    </div>
                  </div>

                  {/* Selector Multi-mes Individual */}
                  {multiMesEmpresaId === id && (
                    <div className="mt-3 pt-3 border-t animate__animated animate__fadeIn animate__faster">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-indigo-700 font-bold">Selecciona meses ({anio}) para esta empresa:</span>
                        <button onClick={() => setMultiMesEmpresaId(null)} className="text-xs text-gray-400 hover:text-gray-600">✕ Cancelar</button>
                      </div>
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-wrap gap-1">
                          {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                            <button key={m} 
                              onClick={() => setMesesMultiEmpresa(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m])}
                              className={`px-3 py-1.5 text-xs font-bold rounded-md border shadow-sm transition-all ${mesesMultiEmpresa.includes(m) ? 'bg-indigo-600 text-white border-indigo-700 scale-105' : 'bg-white text-gray-600 border-gray-300 hover:bg-indigo-50 hover:border-indigo-300'}`}>
                              {MESES[m].substring(0,3)}
                            </button>
                          ))}
                        </div>
                        <div className="flex flex-col gap-2 p-3 bg-indigo-50/50 rounded-lg border border-indigo-100">
                          <div className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider mb-1">Opciones de Ejecución Multi-mes</div>
                          <div className="flex flex-wrap gap-4">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="checkbox" checked={manualOpciones.compras} onChange={e => setManualOpciones(p => ({...p, compras: e.target.checked}))} className="accent-indigo-600 rounded" />
                              <span className="text-xs font-semibold text-indigo-900">Compras</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="checkbox" checked={manualOpciones.ventas} onChange={e => setManualOpciones(p => ({...p, ventas: e.target.checked}))} className="accent-indigo-600 rounded" />
                              <span className="text-xs font-semibold text-indigo-900">Ventas</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="checkbox" checked={manualOpciones.honorarios} onChange={e => setManualOpciones(p => ({...p, honorarios: e.target.checked}))} className="accent-indigo-600 rounded" />
                              <span className="text-xs font-semibold text-indigo-900">Honorarios</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="checkbox" checked={manualOpciones.centralizacion} onChange={e => setManualOpciones(p => ({...p, centralizacion: e.target.checked}))} disabled={manualOpciones.soloFetch} className="accent-indigo-600 rounded disabled:opacity-40" />
                              <span className={`text-xs font-semibold ${manualOpciones.soloFetch ? 'text-indigo-300' : 'text-indigo-900'}`}>Centralización</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer border-l border-indigo-200 pl-4 ml-2">
                              <input type="checkbox" checked={manualOpciones.soloFetch} onChange={e => setManualOpciones(p => ({...p, soloFetch: e.target.checked, centralizacion: e.target.checked ? false : p.centralizacion}))} className="accent-slate-600 rounded" />
                              <span className="text-xs font-bold text-slate-700">☁️ Solo Descargar</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer border-l border-indigo-200 pl-4 ml-2">
                              <input type="checkbox" checked={manualOpciones.forzarReejecucion} onChange={e => setManualOpciones(p => ({...p, forzarReejecucion: e.target.checked}))} className="accent-yellow-600 rounded" />
                              <span className="text-xs font-bold text-yellow-700">⚡ Forzar</span>
                            </label>
                          </div>
                          <div className="mt-2 flex justify-end">
                            <button onClick={() => ejecutarMultiMesCustom(id)}
                              disabled={mesesMultiEmpresa.length === 0}
                              className="px-6 py-2 text-xs font-bold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white shadow-md transition-all disabled:opacity-50 flex items-center gap-2">
                              ▶ Ejecutar los {mesesMultiEmpresa.length} meses seleccionados
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── Sub-tab: Configuración ── */}
      {subTab === 'config' && (
        empresasLista.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border">
            <p className="text-gray-400 text-sm">Esta lista no tiene empresas.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {empresasLista.map(e => {
              const id = (e._id || e).toString();
              const cfg = configMap[id];
              const isExp = expandedConfig === id;
              const tieneId = !!cfg?.inputsoftEmpresaId;
              return (
                <div key={id} className="bg-white border rounded-xl overflow-hidden shadow-sm">
                  <button onClick={() => setExpandedConfig(isExp ? null : id)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${tieneId ? 'bg-green-500' : 'bg-orange-400'}`} />
                      <div className="min-w-0">
                        <div className="font-medium text-gray-800 text-sm truncate">{e.nombre}</div>
                        <div className="text-xs text-gray-400 font-mono">{e.rut}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      {tieneId
                        ? <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full font-medium hidden sm:inline">#{cfg.inputsoftEmpresaId}</span>
                        : <span className="text-xs px-2 py-0.5 bg-orange-50 text-orange-600 border border-orange-200 rounded-full hidden sm:inline">Sin configurar</span>
                      }
                      <span className="text-gray-400 text-xs">{isExp ? '▲' : '▼'}</span>
                    </div>
                  </button>
                  {isExp && (
                    <div className="px-4 pb-4 border-t bg-gray-50/50">
                      <div className="pt-4">
                        <ConfigEmpresaInline
                          clienteId={id}
                          initialConfig={cfg}
                          usuarios={usuarios}
                          isAdmin={isAdmin}
                          onGuardado={onGuardado}
                          onOpenDashboard={onOpenDashboard}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── Sub-tab: Batch ── */}
      {subTab === 'batch' && isAdmin && (
        <BatchManager
          companies={empresasLista}
          configMap={configMap}
          mes={mes}
          anio={anio}
          onGuardado={onGuardado}
          onEjecutar={() => onEjecutar(lista)}
          isExecutingBatch={isExecutingBatch}
        />
      )}

      {/* ── Sub-tab: Historial ── */}
      {subTab === 'historial' && <HistorialLogs logs={logsLista} onRefresh={onGuardado} />}
    </div>
  );
}

function ModalEjecutarLote({ lista, onClose, onEjecutar }) {
  const [opciones, setOpciones] = useState({
    useDefault: true,
    ventas: true,
    compras: true,
    honorarios: true,
    centralizacion: true,
    soloFetch: false,
    forzarReejecucion: false
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate__animated animate__zoomIn animate__faster">
        <div className="bg-slate-800 px-6 py-4 flex justify-between items-center">
          <h2 className="text-white font-semibold flex items-center gap-2">
            <span className="text-xl">⚡</span> Ejecutar Lote
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">✕</button>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-5">
            Selecciona cómo deseas ejecutar las empresas de este lote.
          </p>

          <div className="space-y-4">
            <label className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${opciones.useDefault ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:bg-gray-50'}`}>
              <input type="radio" checked={opciones.useDefault} onChange={() => setOpciones(p => ({...p, useDefault: true}))} className="mt-1 accent-emerald-600 w-4 h-4" />
              <div>
                <div className="font-semibold text-gray-800">Usar configuración por defecto</div>
                <div className="text-xs text-gray-500">Cada empresa ejecutará exactamente lo que tenga configurado en su perfil individual. (Recomendado)</div>
              </div>
            </label>

            <label className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${!opciones.useDefault ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
              <input type="radio" checked={!opciones.useDefault} onChange={() => setOpciones(p => ({...p, useDefault: false}))} className="mt-1 accent-blue-600 w-4 h-4" />
              <div className="w-full">
                <div className="font-semibold text-gray-800">Forzar manualmente</div>
                <div className="text-xs text-gray-500 mb-3">Ignora la configuración individual y fuerza a todas las empresas a ejecutar lo siguiente:</div>
                
                <div className={`grid grid-cols-2 gap-3 p-3 bg-white rounded border transition-opacity ${opciones.useDefault ? 'opacity-50 pointer-events-none' : ''}`}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={opciones.compras} onChange={e => setOpciones(p => ({...p, compras: e.target.checked}))} className="accent-blue-600 rounded" />
                    <span className="text-sm font-medium">Compras</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={opciones.ventas} onChange={e => setOpciones(p => ({...p, ventas: e.target.checked}))} className="accent-blue-600 rounded" />
                    <span className="text-sm font-medium">Ventas</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={opciones.honorarios} onChange={e => setOpciones(p => ({...p, honorarios: e.target.checked}))} className="accent-blue-600 rounded" />
                    <span className="text-sm font-medium">Honorarios</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={opciones.centralizacion} onChange={e => setOpciones(p => ({...p, centralizacion: e.target.checked}))} disabled={opciones.soloFetch} className="accent-blue-600 rounded" />
                    <span className="text-sm font-medium">Centralización</span>
                  </label>
                </div>

                <div className={`mt-3 p-2 bg-slate-100 border border-slate-300 rounded transition-opacity ${opciones.useDefault ? 'opacity-50 pointer-events-none' : ''}`}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={opciones.soloFetch} onChange={e => {
                      setOpciones(p => ({...p, soloFetch: e.target.checked, centralizacion: e.target.checked ? false : p.centralizacion}));
                    }} className="accent-slate-600 rounded" />
                    <span className="text-sm font-semibold text-slate-700">☁️ Solo Descargar (Ignorar Inputsoft)</span>
                  </label>
                </div>
              </div>
            </label>

            <div className="flex items-center gap-2 mb-4 p-2.5 bg-yellow-50 text-yellow-800 text-xs rounded border border-yellow-200">
              <input type="checkbox" id="loteForzar" checked={opciones.forzarReejecucion}
                onChange={e => setOpciones(p => ({...p, forzarReejecucion: e.target.checked}))}
                className="w-4 h-4 text-yellow-600 rounded" />
              <label htmlFor="loteForzar" className="font-medium cursor-pointer">
                Forzar re-ejecución (Correr incluso los que ya están Completados)
              </label>
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50 font-medium">
              Cancelar
            </button>
            <button 
              onClick={() => onEjecutar(opciones)}
              className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold shadow-md flex justify-center items-center gap-2"
            >
              ▶ Iniciar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────────────
// EDITOR DE LISTA (crear / editar)
// ─────────────────────────────────────────────────────────────────────────────
function ListaEditor({ lista, companies, configMap, onSave, onCancel }) {
  const [nombre, setNombre] = useState(lista.nombre || '');
  const [descripcion, setDescripcion] = useState(lista.descripcion || '');
  const [selectedIds, setSelectedIds] = useState(
    new Set((lista.empresas || []).map(e => (e._id || e).toString()))
  );
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const filtered = companies.filter(c =>
    !search ||
    c.nombre.toLowerCase().includes(search.toLowerCase()) ||
    c.rut.includes(search)
  );

  const toggle = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (!nombre.trim()) return toast.error('El nombre es requerido');
    setSaving(true);
    await onSave({
      _id: lista._id,
      nombre: nombre.trim(),
      descripcion: descripcion.trim(),
      empresas: Array.from(selectedIds),
    });
    setSaving(false);
  };

  return (
    <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
      {/* Cabecera */}
      <div className="px-4 sm:px-6 py-4 border-b flex items-center justify-between">
        <h2 className="font-semibold text-gray-800 text-base">
          {lista._id ? 'Editar lista' : 'Nueva lista'}
        </h2>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
      </div>

      <div className="px-4 sm:px-6 py-4 space-y-4">
        {/* Nombre + descripción */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Nombre *</label>
            <input
              type="text"
              value={nombre}
              onChange={e => setNombre(e.target.value)}
              placeholder="ej: IVA mensual, RCV enero, F29…"
              autoFocus
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Descripción</label>
            <input
              type="text"
              value={descripcion}
              onChange={e => setDescripcion(e.target.value)}
              placeholder="Opcional…"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none" />
          </div>
        </div>

        {/* Selector de empresas */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-500">
              Empresas ({selectedIds.size} seleccionadas)
            </label>
            {selectedIds.size > 0 && (
              <button onClick={() => setSelectedIds(new Set())}
                className="text-xs text-gray-400 hover:text-gray-600 underline">
                Limpiar selección
              </button>
            )}
          </div>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar empresa…"
            className="w-full border rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none" />
          <div className="max-h-72 overflow-y-auto border rounded-lg divide-y">
            {filtered.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-6">Sin resultados</p>
            ) : filtered.map(c => {
              const id = c._id.toString();
              const selected = selectedIds.has(id);
              const tieneId = !!configMap[c._id]?.inputsoftEmpresaId;
              return (
                <label key={c._id}
                  className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${selected ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}>
                  <input type="checkbox" checked={selected} onChange={() => toggle(id)}
                    className="accent-blue-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-gray-800 truncate">{c.nombre}</div>
                    <div className="text-xs text-gray-400 font-mono">{c.rut}</div>
                  </div>
                  {tieneId ? (
                    <span className="text-xs text-green-600 font-medium flex-shrink-0">✓</span>
                  ) : (
                    <span className="text-xs text-orange-400 flex-shrink-0">sin ID</span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 sm:px-6 py-3 border-t bg-gray-50 flex flex-wrap gap-2">
        <button onClick={handleSave} disabled={saving}
          className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors">
          {saving ? 'Guardando…' : lista._id ? 'Actualizar lista' : 'Crear lista'}
        </button>
        <button onClick={onCancel}
          className="px-4 py-2 border text-gray-500 hover:bg-white text-sm rounded-lg transition-colors">
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG POR EMPRESA (inline expandible)
// ─────────────────────────────────────────────────────────────────────────────
function ConfigEmpresaInline({ clienteId, initialConfig, usuarios, isAdmin, onGuardado }) {
  const [cfg, setCfg] = useState({
    inputsoftEmpresaId: initialConfig?.inputsoftEmpresaId || '',
    sucursalId: initialConfig?.sucursalId || '',
    cuentaDefaultCompra: initialConfig?.cuentaDefaultCompra || '',
    cuentaDefaultVenta: initialConfig?.cuentaDefaultVenta || '',
    cuentaHonorarios: initialConfig?.cuentaHonorarios || '',
    modoImpuestos: initialConfig?.modoImpuestos || 'exento',
    impuestosAMantener: (initialConfig?.impuestosAMantener || []).join(', '),
    activoBatch: initialConfig?.activoBatch || false,
    ejecutarVentas: initialConfig?.ejecutarVentas ?? true,
    ejecutarCompras: initialConfig?.ejecutarCompras ?? true,
    ejecutarHonorarios: initialConfig?.ejecutarHonorarios ?? false,
    soloFetch: initialConfig?.soloFetch ?? false,
    centralizarAutomatico: initialConfig?.centralizarAutomatico || false,
    usuariosAsignados: (initialConfig?.usuariosAsignados || []).map(u => u._id || u),
  });
  const [guardando, setGuardando] = useState(false);
  const [subTab, setSubTab] = useState('config');

  const guardar = async () => {
    if (!cfg.inputsoftEmpresaId.trim()) return toast.error('El ID Inputsoft es obligatorio');
    setGuardando(true);
    try {
      const payload = {
        ...cfg,
        impuestosAMantener: cfg.modoImpuestos === 'mantener_especificos'
          ? cfg.impuestosAMantener.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        usuariosAsignados: cfg.usuariosAsignados,
      };
      await api.put(`/contabilidad/empresas/${clienteId}`, payload);
      toast.success('Configuración guardada');
      onGuardado();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error guardando');
    } finally {
      setGuardando(false);
    }
  };

  const toggleUsuario = (userId) => {
    setCfg(p => ({
      ...p,
      usuariosAsignados: p.usuariosAsignados.includes(userId)
        ? p.usuariosAsignados.filter(id => id !== userId)
        : [...p.usuariosAsignados, userId]
    }));
  };

  return (
    <div>
      {/* Sub-tabs */}
      <div className="flex gap-3 mb-4 border-b border-blue-100 overflow-x-auto">
        {[['config', 'Datos contables'], ['usuarios', 'Contadores'], ['reglas', 'Reglas']].map(([id, label]) => (
          <button key={id} onClick={() => setSubTab(id)}
            className={`pb-2 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap ${subTab === id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
            {label}
          </button>
        ))}
      </div>

      {subTab === 'config' && (
        <div className="space-y-4">
          {/* IDs */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">ID Inputsoft *</label>
              <input type="text" value={cfg.inputsoftEmpresaId}
                onChange={e => setCfg(p => ({ ...p, inputsoftEmpresaId: e.target.value }))}
                placeholder="ej: 125" className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">ID Sucursal</label>
              <input type="text" value={cfg.sucursalId}
                onChange={e => setCfg(p => ({ ...p, sucursalId: e.target.value }))}
                placeholder="vacío = CASA MATRIZ" className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Cta. default Compra</label>
              <input type="text" value={cfg.cuentaDefaultCompra}
                onChange={e => setCfg(p => ({ ...p, cuentaDefaultCompra: e.target.value }))}
                placeholder="ej: 4110" className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Cta. default Venta</label>
              <input type="text" value={cfg.cuentaDefaultVenta}
                onChange={e => setCfg(p => ({ ...p, cuentaDefaultVenta: e.target.value }))}
                placeholder="ej: 7100" className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Cta. Honorarios</label>
              <input type="text" value={cfg.cuentaHonorarios}
                onChange={e => setCfg(p => ({ ...p, cuentaHonorarios: e.target.value }))}
                placeholder="ej: 3210" className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
          </div>

          {/* Modo impuestos */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">Manejo de impuestos en CSV</label>

            <div className="space-y-2">
              {[
                { value: 'exento', label: 'Todo a exento', desc: 'Suma todos los impuestos en la columna Exento.' },
                { value: 'columnas_xyz', label: 'Columnas de impuesto (XYZ)', desc: 'Conserva las columnas código/valor/tasa tal como vienen del SII.' },
                { value: 'mantener_especificos', label: 'Mantener impuestos específicos', desc: 'Los códigos indicados conservan columnas; el resto va a Exento.' },
              ].map(opt => (
                <label key={opt.value} className="flex items-start gap-2 cursor-pointer">
                  <input type="radio" value={opt.value} checked={cfg.modoImpuestos === opt.value}
                    onChange={() => setCfg(p => ({ ...p, modoImpuestos: opt.value }))}
                    className="accent-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="flex-1">
                    <span className="text-sm font-medium">{opt.label}</span>
                    <span className="block text-xs text-gray-400">{opt.desc}</span>
                    {opt.value === 'mantener_especificos' && cfg.modoImpuestos === 'mantener_especificos' && (
                      <input type="text" value={cfg.impuestosAMantener}
                        onChange={e => setCfg(p => ({ ...p, impuestosAMantener: e.target.value }))}
                        placeholder="ej: 14, 15  (separados por coma)"
                        className="mt-1.5 w-full max-w-xs border rounded px-2 py-1 text-xs" />
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Checkboxes de automatización */}
          <div className="pt-2 border-t border-gray-100 flex flex-col gap-2">
            <label className="flex items-center gap-2 cursor-pointer bg-amber-50 border border-amber-200 p-2 rounded w-fit">
              <input type="checkbox" checked={cfg.activoBatch}
                onChange={e => setCfg(p => ({ ...p, activoBatch: e.target.checked }))}
                className="accent-amber-600 w-4 h-4" />
              <span className="text-sm font-semibold text-amber-800">Habilitar para Batch (Multi-empresa)</span>
            </label>
            <div className="flex flex-wrap gap-4 bg-gray-50 p-3 rounded border mt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={cfg.ejecutarVentas}
                  onChange={e => setCfg(p => ({ ...p, ejecutarVentas: e.target.checked }))}
                  className="accent-emerald-600 w-4 h-4" />
                <span className="text-sm font-medium">Procesar Ventas</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={cfg.ejecutarCompras}
                  onChange={e => setCfg(p => ({ ...p, ejecutarCompras: e.target.checked }))}
                  className="accent-emerald-600 w-4 h-4" />
                <span className="text-sm font-medium">Procesar Compras</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={cfg.ejecutarHonorarios}
                  onChange={e => setCfg(p => ({ ...p, ejecutarHonorarios: e.target.checked }))}
                  className="accent-emerald-600 w-4 h-4" />
                <span className="text-sm font-medium">Procesar Honorarios</span>
              </label>
            </div>
            <div className="flex flex-col gap-2 mt-2">
              <label className="flex items-center gap-2 cursor-pointer bg-blue-50 border border-blue-200 p-2 rounded w-fit">
                <input type="checkbox" checked={cfg.centralizarAutomatico}
                  onChange={e => setCfg(p => ({ ...p, centralizarAutomatico: e.target.checked }))}
                  className="accent-blue-600 w-4 h-4" />
                <span className="text-sm font-semibold text-blue-800">Centralizar Automáticamente al finalizar</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer bg-slate-50 border border-slate-200 p-2 rounded w-fit">
                <input type="checkbox" checked={cfg.soloFetch}
                  onChange={e => setCfg(p => ({ ...p, soloFetch: e.target.checked }))}
                  className="accent-slate-600 w-4 h-4" />
                <span className="text-sm font-semibold text-slate-800">☁️ Solo Descargar (Ignorar Inputsoft en Batch)</span>
              </label>
            </div>
          </div>

          {isAdmin && (
            <button onClick={guardar} disabled={guardando}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-4 py-1.5 rounded-lg disabled:opacity-50">
              {guardando ? 'Guardando…' : 'Guardar configuración'}
            </button>
          )}
        </div>
      )}

      {subTab === 'usuarios' && (
        <div>
          <p className="text-xs text-gray-500 mb-3">
            Contadores que pueden ver y ejecutar esta empresa.
            {!isAdmin && <span className="block text-orange-500 mt-1">Solo el administrador puede modificar.</span>}
          </p>
          {usuarios.length === 0 ? (
            <p className="text-xs text-gray-400">No hay usuarios disponibles.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-4">
              {usuarios.map(u => {
                const assigned = cfg.usuariosAsignados.includes(u._id);
                return (
                  <label key={u._id}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${assigned ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                      } ${!isAdmin ? 'pointer-events-none opacity-70' : ''}`}>
                    <input type="checkbox" checked={assigned} disabled={!isAdmin}
                      onChange={() => toggleUsuario(u._id)} className="accent-blue-600" />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-800 truncate">{u.nombre}</span>
                      <span className="block text-xs text-gray-400">{u.role}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          {isAdmin && (
            <button onClick={guardar} disabled={guardando}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-4 py-1.5 rounded-lg disabled:opacity-50">
              {guardando ? 'Guardando…' : 'Guardar asignación'}
            </button>
          )}
        </div>
      )}

      {subTab === 'reglas' && <ReglasContables clienteId={clienteId} />}
    </div>
  );
}

function BatchManager({ companies, configMap, mes, anio, onGuardado, onEjecutar, isExecutingBatch }) {
  const [search, setSearch] = useState('');
  const [guardandoId, setGuardandoId] = useState('');

  const filtradas = companies.filter(c =>
    !search ||
    (c.nombre || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.rut || '').includes(search)
  );

  const activas = companies.filter(c => configMap[c._id]?.activoBatch);
  const activasConInputsoft = activas.filter(c => configMap[c._id]?.inputsoftEmpresaId);

  const actualizarBatch = async (clienteId, activoBatch) => {
    const cfg = configMap[clienteId] || {};
    setGuardandoId(clienteId);
    try {
      await api.put(`/contabilidad/empresas/${clienteId}`, {
        inputsoftEmpresaId: cfg.inputsoftEmpresaId || '',
        sucursalId: cfg.sucursalId || '',
        cuentaDefaultCompra: cfg.cuentaDefaultCompra || '',
        cuentaDefaultVenta: cfg.cuentaDefaultVenta || '',
        modoImpuestos: cfg.modoImpuestos || 'exento',
        impuestosAMantener: cfg.impuestosAMantener || [],
        activoBatch,
        centralizarAutomatico: cfg.centralizarAutomatico || false,
        notas: cfg.notas || '',
        usuariosAsignados: (cfg.usuariosAsignados || []).map(u => u._id || u)
      });
      toast.success(activoBatch ? 'Empresa agregada al batch' : 'Empresa quitada del batch');
      onGuardado();
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo actualizar el batch');
    } finally {
      setGuardandoId('');
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        Batch y listas manuales son cosas distintas. Solo las empresas marcadas aquí entran al batch.
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-400">Empresas batch</div>
          <div className="mt-2 text-2xl font-semibold text-gray-800">{activas.length}</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-400">Listas para ejecutar</div>
          <div className="mt-2 text-2xl font-semibold text-gray-800">{activasConInputsoft.length}</div>
          <div className="text-xs text-gray-400">con ID Inputsoft</div>
        </div>
        <div className="rounded-xl border bg-white p-4 md:col-span-2">
          <div className="mt-3">
            <button
              onClick={() => onEjecutar(companies)}
              disabled={activasConInputsoft.length === 0 || isExecutingBatch}
              className="rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-bold shadow hover:shadow-md text-white transition-all hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg leading-none">⚡</span> 
                {isExecutingBatch ? 'Iniciando Lote...' : 'Ejecutar Lote Completo'}
              </div>
            </button>
            <div className="mt-2 text-xs text-gray-400">Período global: {MESES[mes]} {anio}</div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Selector de empresas batch</h3>
            <p className="text-xs text-gray-400">Marca aquí qué empresas entran al batch. No afecta tus listas manuales.</p>
          </div>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar empresa o RUT..."
            className="w-full max-w-xs rounded-lg border px-3 py-2 text-sm"
          />
        </div>

        <div className="overflow-hidden rounded-lg border">
          <div className="grid grid-cols-[minmax(0,1.8fr)_120px_130px] gap-3 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <div>Empresa</div>
            <div>Inputsoft</div>
            <div>Batch</div>
          </div>
          <div className="max-h-[28rem] overflow-y-auto divide-y">
            {filtradas.map(c => {
              const cfg = configMap[c._id] || {};
              const checked = Boolean(cfg.activoBatch);
              const tieneInputsoft = Boolean(cfg.inputsoftEmpresaId);
              return (
                <div key={c._id} className="grid grid-cols-[minmax(0,1.8fr)_120px_130px] items-center gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-gray-800">{c.nombre}</div>
                    <div className="text-xs font-mono text-gray-400">{c.rut}</div>
                  </div>
                  <div>
                    {tieneInputsoft ? (
                      <span className="inline-flex rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                        #{cfg.inputsoftEmpresaId}
                      </span>
                    ) : (
                      <span className="inline-flex rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-600">
                        Sin ID
                      </span>
                    )}
                  </div>
                  <div>
                    <button
                      onClick={() => actualizarBatch(c._id, !checked)}
                      disabled={guardandoId === c._id}
                      className={`inline-flex min-w-[110px] items-center justify-center rounded-lg px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-50 ${checked
                          ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                      {guardandoId === c._id ? 'Guardando...' : checked ? 'En batch' : 'Fuera batch'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HISTORIAL DE EJECUCIONES
// ─────────────────────────────────────────────────────────────────────────────
function HistorialLogs({ logs, onRefresh }) {
  const [logDetalle, setLogDetalle] = useState(null);
  const [archivosDetalle, setArchivosDetalle] = useState([]);
  const [descargandoArchivo, setDescargandoArchivo] = useState('');
  const [mostrarHistorialCompleto, setMostrarHistorialCompleto] = useState(false);
  const logsOrdenados = [...logs].sort((a, b) => {
    const getRank = (estado) => {
      if (estado === 'en_progreso') return 1;
      if (estado === 'en_cola') return 2;
      return 3;
    };
    const rankA = getRank(a.estado);
    const rankB = getRank(b.estado);
    
    if (rankA !== rankB) return rankA - rankB;
    
    if (rankA === 2) {
      // en_cola: el más antiguo primero
      return new Date(a.fechaInicio) - new Date(b.fechaInicio);
    }
    // resto: el más reciente primero
    return new Date(b.fechaInicio) - new Date(a.fechaInicio);
  });
  
  const activosCount = logsOrdenados.filter(l => ['en_progreso', 'en_cola'].includes(l.estado)).length;
  const minToShow = Math.max(8, activosCount);
  const logsAMostrar = mostrarHistorialCompleto ? logsOrdenados : logsOrdenados.slice(0, minToShow);
  const logsOcultos = Math.max(logsOrdenados.length - logsAMostrar.length, 0);

  const verDetalle = async (logId) => {
    try {
      const [logRes, archivosRes] = await Promise.all([
        api.get(`/contabilidad/logs/${logId}`),
        api.get(`/contabilidad/logs/${logId}/archivos`)
      ]);
      setLogDetalle(logRes.data);
      setArchivosDetalle(archivosRes.data?.archivos || []);
    } catch (_) { toast.error('Error cargando detalle'); }
  };

  const cerrarDetalle = () => {
    setLogDetalle(null);
    setArchivosDetalle([]);
    setDescargandoArchivo('');
  };

  const detenerEjecucion = async (logId) => {
    try {
      await api.post(`/contabilidad/cancelar/${logId}`);
      toast.success('Se ha enviado la señal para detener la ejecución');
      onRefresh(true);
      if (logDetalle?._id === logId) verDetalle(logId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo detener la ejecución');
    }
  };

  const cambiarEstadoLog = async (logId, nuevoEstado) => {
    try {
      await api.put(`/contabilidad/logs/${logId}/estado`, { estado: nuevoEstado });
      toast.success(`Estado cambiado a ${nuevoEstado}`);
      onRefresh(true);
      if (logDetalle?._id === logId) verDetalle(logId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo cambiar el estado');
    }
  };

  const descargarArchivo = async (fileName) => {
    if (!logDetalle?._id) return;
    setDescargandoArchivo(fileName);
    try {
      const response = await api.get(
        `/contabilidad/logs/${logDetalle._id}/archivos/${encodeURIComponent(fileName)}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo descargar el archivo');
    } finally {
      setDescargandoArchivo('');
    }
  };

  const reejecutarProceso = async (clienteId, tipo, overrideMes, overrideAnio) => {
    toast.info(`Iniciando proceso...`);
    try {
      await api.post('/contabilidad/ejecutar', {
        clienteId, mes: overrideMes, anio: overrideAnio, tipoDocumento: tipo
      });
      toast.success('Proceso re-encolado exitosamente');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo re-ejecutar el proceso');
    }
  };

  useEffect(() => {
    if (logsAMostrar.length === 0) {
      setLogDetalle(null);
      setArchivosDetalle([]);
      setDescargandoArchivo('');
      return;
    }

    const logVisible = logsAMostrar.some(log => log._id === logDetalle?._id);
    if (!logVisible) {
      verDetalle(logsAMostrar[0]._id);
    }
  }, [logs, mostrarHistorialCompleto]);

  useEffect(() => {
    if (!logDetalle?._id) return undefined;

    const logActual = logs.find(log => log._id === logDetalle._id);
    if (!logActual || (logActual.estado !== 'en_progreso' && logActual.estado !== 'en_cola')) return undefined;

    const interval = setInterval(() => {
      verDetalle(logDetalle._id);
    }, 3000);

    return () => clearInterval(interval);
  }, [logs, logDetalle?._id]);

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-base font-semibold text-gray-700">Historial de ejecuciones</h2>
        <div className="flex gap-2">
          {logsOcultos > 0 && (
            <button
              onClick={() => setMostrarHistorialCompleto(!mostrarHistorialCompleto)}
              className="text-sm text-gray-600 hover:text-gray-800 px-3 py-1 border rounded"
            >
              {mostrarHistorialCompleto ? 'Ocultar historial antiguo' : `Mostrar historial (${logsOcultos} más)`}
            </button>
          )}
          <button onClick={onRefresh} className="text-sm text-blue-600 hover:text-blue-800">↻ Actualizar</button>
        </div>
      </div>

      {/* Banner de estado de ejecución */}
      {logs.filter(l => ['en_progreso', 'en_cola'].includes(l.estado)).length > 0 ? (
        <div className="mb-4 flex items-center justify-between rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="relative flex h-3 w-3">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75"></span>
              <span className="relative inline-flex h-3 w-3 rounded-full bg-blue-500"></span>
            </div>
            <span className="text-sm font-medium text-blue-900">
              <strong>Batch en ejecución:</strong> {logs.filter(l => ['en_progreso', 'en_cola'].includes(l.estado)).length} procesos en progreso o cola.
            </span>
          </div>
        </div>
      ) : (
        <div className="mb-4 flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="relative inline-flex h-3 w-3 rounded-full bg-gray-400"></span>
            <span className="text-sm font-medium text-gray-600">
              Sistema inactivo (sin ejecuciones pendientes)
            </span>
          </div>
        </div>
      )}

      {logDetalle && (
        <div className="mb-4 bg-gray-900 text-gray-100 rounded-xl p-4 text-xs font-mono overflow-x-auto">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-2">
              <span className="font-bold text-emerald-400">
                {logDetalle.clienteId?.nombre || logDetalle.nombreEmpresa} — {MESES[logDetalle.mes]} {logDetalle.anio} — {logDetalle.tipoDocumento}
              </span>
              <select
                value=""
                onChange={(e) => {
                  if(e.target.value) cambiarEstadoLog(logDetalle._id, e.target.value);
                }}
                className="text-[10px] bg-transparent border-none text-gray-500 hover:text-gray-300 cursor-pointer appearance-none text-center outline-none p-0 ml-1"
                title="Forzar cambio de estado"
              >
                <option value="">⚙️ Estado</option>
                <option value="en_cola" className="text-gray-900 bg-white">Forzar: En cola</option>
                <option value="en_progreso" className="text-gray-900 bg-white">Forzar: En progreso</option>
                <option value="completado" className="text-gray-900 bg-white">Forzar: Completado</option>
                <option value="error" className="text-gray-900 bg-white">Forzar: Error</option>
                <option value="cancelado" className="text-gray-900 bg-white">Forzar: Cancelado</option>
              </select>
            </div>
            <div className="flex items-center gap-4">
              {(logDetalle.estado !== 'en_progreso' && logDetalle.estado !== 'en_cola') && (
                <button
                  onClick={() => {
                    const id = logDetalle.clienteId?._id || logDetalle.clienteId;
                    if (id) {
                      reejecutarProceso(id, logDetalle.tipoDocumento, logDetalle.mes, logDetalle.anio);
                      cerrarDetalle();
                    }
                  }}
                  className="px-2 py-1 bg-blue-900/50 hover:bg-blue-800 text-blue-200 border border-blue-700 rounded text-[10px] uppercase font-bold tracking-wider transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <i className="material-symbols-outlined text-[12px]">replay</i> Re-ejecutar
                  </div>
                </button>
              )}
              {(logDetalle.estado === 'en_progreso' || logDetalle.estado === 'en_cola') && (
                <button
                  onClick={() => detenerEjecucion(logDetalle._id)}
                  className="px-2 py-1 bg-red-900/50 hover:bg-red-800 text-red-200 border border-red-700 rounded text-[10px] uppercase font-bold tracking-wider"
                >
                  Detener
                </button>
              )}
              <button onClick={cerrarDetalle} className="text-gray-400 hover:text-white text-lg leading-none">✕</button>
            </div>
          </div>
          {logDetalle.descargaSii?.sinDatos && (
            <div className="mb-3 rounded-lg bg-slate-800 px-3 py-2 text-slate-100">
              {logDetalle.descargaSii?.mensaje || `No hay información de ${logDetalle.tipoDocumento} en este período`}
            </div>
          )}

          {/* Errores Genéricos */}
          {(() => {
            const erroresGenerales = [];
            if (logDetalle.descargaSii?.error) erroresGenerales.push(`SII: ${logDetalle.descargaSii.error}`);
            if (logDetalle.procesamientoCsv?.mensaje) erroresGenerales.push(`CSV: ${logDetalle.procesamientoCsv.mensaje}`);
            if (logDetalle.subidaInputsoft?.error) erroresGenerales.push(`Inputsoft CSV: ${logDetalle.subidaInputsoft.error}`);
            if (logDetalle.ingresoBoletas?.error && logDetalle.ingresoBoletas.error !== 'YA_EXISTE_SUCCESS') erroresGenerales.push(`Inputsoft Boletas: ${logDetalle.ingresoBoletas.error}`);
            
            if (erroresGenerales.length === 0) return null;
            return (
              <div className="mb-3 rounded-lg bg-red-900/20 border border-red-800 px-3 py-3 text-red-200 text-sm">
                <div className="font-bold flex items-center gap-1 mb-2">
                  <i className="material-symbols-outlined text-base">warning</i> Ocurrió un problema
                </div>
                <ul className="list-disc pl-5 space-y-1">
                  {erroresGenerales.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
                {logDetalle.honorarios?.errorScreenshot && (
                  <SecureImagePreview logId={logDetalle._id} filename={logDetalle.honorarios.errorScreenshot} />
                )}
              </div>
            );
          })()}
          <div className="flex flex-wrap gap-2 mb-3">
            {[['descargaSii', 'Descarga SII'], ['procesamientoCsv', 'CSV'], ['subidaInputsoft', 'Inputsoft Facturas'], ['ingresoBoletas', 'Inputsoft Boletas']].map(([key, label]) => {
              const etapa = logDetalle[key];
              if (!etapa) return null;
              return (
                <div key={key} className={`px-3 py-1 rounded text-xs font-medium ${etapa.ok ? 'bg-green-800 text-green-100' : etapa.ok === false ? 'bg-red-800 text-red-100' : 'bg-gray-700 text-gray-300'
                  }`}>
                  {etapa.ok ? '✓' : etapa.ok === false ? '✗' : '…'} {label}
                  {etapa.sinDatos ? ' (sin datos)' : ''}
                  {etapa.filasProcesadas != null && ` (${etapa.filasProcesadas} f.)`}
                </div>
              );
            })}
          </div>
          <div className="max-h-72 overflow-y-auto space-y-0.5 pr-1">
            {(logDetalle.logEntries || []).map((entry, i) => (
              <div key={i} className="text-gray-300 leading-relaxed">{entry}</div>
            ))}
          </div>

          {logDetalle.resumenSii && logDetalle.resumenSii.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold text-emerald-400 flex items-center gap-2">
                  <i className="material-symbols-outlined text-sm">receipt_long</i> Resumen del Período (SII)
                </span>
                <span className="text-xs bg-emerald-900/50 border border-emerald-800 text-emerald-200 px-3 py-1 rounded-full shadow-sm">
                  {logDetalle.resumenSii.length} Tipos de Documentos
                </span>
              </div>
              <div className="overflow-x-auto rounded-xl border border-gray-700 bg-gray-800/40 shadow-inner backdrop-blur-sm">
                <table className="w-full text-left text-xs whitespace-nowrap">
                  <thead className="bg-gray-800/80 text-gray-400 uppercase tracking-wider font-semibold">
                    <tr>
                      <th className="px-4 py-3">Tipo de Documento</th>
                      {logDetalle.tipoDocumento === 'honorarios' ? (
                        <>
                          <th className="px-4 py-3 text-right">Cant.</th>
                          <th className="px-4 py-3 text-right">Brutos</th>
                          <th className="px-4 py-3 text-right">Retenido</th>
                          <th className="px-4 py-3 text-right text-emerald-300">Pagado</th>
                        </>
                      ) : (
                        <>
                          <th className="px-4 py-3 text-right">Cant.</th>
                          <th className="px-4 py-3 text-right">Monto Exento</th>
                          <th className="px-4 py-3 text-right">Monto Neto</th>
                          <th className="px-4 py-3 text-right">Monto IVA</th>
                          <th className="px-4 py-3 text-right text-emerald-300">Total</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/50">
                    {logDetalle.resumenSii.map((row, idx) => {
                      const isBoleta = row.tipoDocumento && (row.tipoDocumento.toUpperCase().includes('BOLETA') || row.tipoDocumento.toUpperCase().includes('COMPROBANTE PAGO') || row.tipoDocumento.toUpperCase().includes('HONORARIOS'));
                      
                      return (
                        <tr key={idx} className={`hover:bg-gray-700/50 transition-colors ${isBoleta ? 'bg-indigo-900/20' : ''}`}>
                          <td className="px-4 py-3 font-medium text-gray-200">
                            {isBoleta && <span className="inline-block mr-2 w-2 h-2 rounded-full bg-indigo-400 shadow-[0_0_8px_rgba(129,140,248,0.8)]" title="Boleta detectada" />}
                            {row.tipoDocumento}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-300 font-mono">{row.totalDocumentos || 0}</td>
                          {logDetalle.tipoDocumento === 'honorarios' ? (
                            <>
                              <td className="px-4 py-3 text-right text-gray-400 font-mono">{row.totalBruto || row.montoExento || row.montoNeto || 0}</td>
                              <td className="px-4 py-3 text-right text-gray-400 font-mono">{row.totalRetenido || row.montoNeto || row.montoIva || 0}</td>
                              <td className="px-4 py-3 text-right text-emerald-400 font-mono font-semibold">{row.totalPagado || row.montoIva || row.montoTotal || 0}</td>
                            </>
                          ) : (
                            <>
                              <td className="px-4 py-3 text-right text-gray-400 font-mono">{row.montoExento || 0}</td>
                              <td className="px-4 py-3 text-right text-gray-400 font-mono">{row.montoNeto || 0}</td>
                              <td className="px-4 py-3 text-right text-gray-400 font-mono">{row.montoIva || 0}</td>
                              <td className="px-4 py-3 text-right text-emerald-400 font-mono font-semibold">{row.montoTotal || 0}</td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(() => {
            const erroresFacturas = logDetalle.subidaInputsoft?.detallesError || [];
            const erroresBoletas = logDetalle.ingresoBoletas?.detallesError || [];
            const todosErrores = [...erroresFacturas, ...erroresBoletas];
            
            if (todosErrores.length === 0) return null;

            return (
              <div className="mt-4 pt-4 border-t border-red-900/50">
                <div className="flex items-center gap-2 mb-3">
                  <span className="font-semibold text-red-400 flex items-center gap-2">
                    <i className="material-symbols-outlined text-sm">error</i> Errores por Documento
                  </span>
                  <span className="text-xs bg-red-900/50 border border-red-800 text-red-200 px-3 py-1 rounded-full shadow-sm">
                    {todosErrores.length} documentos fallidos
                  </span>
                </div>
                <div className="overflow-x-auto rounded-xl border border-red-900/50 bg-red-900/10 shadow-inner backdrop-blur-sm">
                  <table className="w-full text-left text-xs whitespace-nowrap">
                    <thead className="bg-red-900/20 text-red-300 uppercase tracking-wider font-semibold">
                      <tr>
                        <th className="px-4 py-3">Folio</th>
                        <th className="px-4 py-3">Tipo</th>
                        <th className="px-4 py-3">Motivo de Rechazo (Inputsoft)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-900/30">
                      {todosErrores.map((err, idx) => (
                        <tr key={idx} className="hover:bg-red-900/20 transition-colors">
                          <td className="px-4 py-3 font-medium text-red-200">{err.folio}</td>
                          <td className="px-4 py-3 text-red-300">{err.tipo}</td>
                          <td className="px-4 py-3 text-red-300 whitespace-normal min-w-[300px]">{err.motivo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-gray-200">Archivos disponibles</span>
              <span className="text-gray-500">{archivosDetalle.length} archivo(s)</span>
            </div>
            {archivosDetalle.length === 0 ? (
              <p className="text-gray-500">No hay archivos guardados para esta ejecución.</p>
            ) : (
              <div className="space-y-2">
                {archivosDetalle.map(file => (
                  <div key={file.nombre} className="flex items-center justify-between gap-3 rounded-lg bg-gray-800/80 px-3 py-2">
                    <div className="min-w-0">
                      <div className="truncate text-gray-100 font-medium">{file.nombre}</div>
                      <div className="text-gray-500">{file.tipo} · {(file.tamano / 1024).toFixed(1)} KB</div>
                    </div>
                    <button
                      onClick={() => descargarArchivo(file.nombre)}
                      disabled={descargandoArchivo === file.nombre}
                      className="shrink-0 rounded-lg border border-gray-600 px-3 py-1.5 text-gray-100 hover:bg-gray-700 disabled:opacity-50"
                    >
                      {descargandoArchivo === file.nombre ? 'Descargando...' : 'Descargar'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {logsAMostrar.length === 0 ? (
        <p className="text-gray-400 text-sm py-8 text-center">No hay ejecuciones para este período.</p>
      ) : (
        <div className="bg-white border rounded-xl overflow-hidden shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[500px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Empresa</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Período</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Tipo</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {logsAMostrar.map(log => {
                const resumen = getResumenLog(log, log.tipoDocumento);
                return (
                  <tr key={log._id} onClick={() => verDetalle(log._id)}
                    className="border-t hover:bg-gray-50 cursor-pointer transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-800 max-w-[180px] truncate">
                      {log.clienteId?.nombre || log.nombreEmpresa}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{MESES[log.mes]} {log.anio}</td>
                    <td className="px-4 py-3 text-gray-600 capitalize">{log.tipoDocumento}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${resumen.color}`}>
                          {resumen.label}
                        </span>
                        <select
                          value=""
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            if(e.target.value) cambiarEstadoLog(log._id, e.target.value);
                          }}
                          className="text-xs bg-transparent border-none text-gray-400 hover:text-gray-700 cursor-pointer appearance-none text-center p-0 ml-1 outline-none"
                          title="Forzar cambio de estado"
                        >
                          <option value="">⚙️</option>
                          <option value="en_cola">Forzar: En cola</option>
                          <option value="en_progreso">Forzar: En progreso</option>
                          <option value="completado">Forzar: Completado</option>
                          <option value="error">Forzar: Error</option>
                          <option value="cancelado">Forzar: Cancelado</option>
                        </select>
                        {(log.estado === 'en_progreso' || log.estado === 'en_cola') && (
                          <button
                            onClick={(e) => { e.stopPropagation(); detenerEjecucion(log._id); }}
                            className="ml-2 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-500 hover:text-red-700 border border-red-200 hover:bg-red-50 rounded transition-colors"
                            title="Detener ejecución"
                          >
                            Detener
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {new Date(log.fechaInicio).toLocaleString('es-CL')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function SelectEstadoMini({ logId, onCambiarEstado }) {
  if (!logId) return null;
  return (
    <select
      value=""
      onChange={(e) => {
        e.stopPropagation();
        if(e.target.value) onCambiarEstado(logId, e.target.value);
      }}
      className="text-[10px] bg-transparent border-none text-gray-300 hover:text-gray-600 cursor-pointer appearance-none outline-none ml-0.5 p-0"
      title="Forzar estado"
    >
      <option value="">⚙️</option>
      <option value="en_cola">En cola</option>
      <option value="en_progreso">En progreso</option>
      <option value="completado">Completado</option>
      <option value="error">Error</option>
    </select>
  );
}
